import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Lock, Store, Utensils, Plus, Trash2, Pencil, Save, X, ChevronDown, ChevronUp, ChevronRight, KeyRound, Sparkles, Upload, BookOpen, Languages, Clock, ArrowUp, ArrowDown, Image as ImageIcon, Eye, EyeOff, CalendarDays, MessageCircle, Phone, Mail, BarChart3, TrendingUp, Users, Euro, Heart, Home as HomeIcon, Zap, QrCode, ExternalLink, Compass, MapPin, Star, ClipboardList, Key, Sunset, Check, CheckCircle2, ShoppingBag, Store as StoreIcon, Gift, Radio } from "lucide-react";
import { fetchRestaurant, fetchMenu, fetchMenus, login, logout, hasToken, verifyToken, updateRestaurant, createCategory, updateCategory, deleteCategory, createDish, updateDish, deleteDish, uploadImage, createMenu, updateMenu, deleteMenu, reorderMenus, reorderCategories, translateText, startBulkTranslate, getTranslateJob, fetchAdminSpaBookings, cancelSpaBooking, deleteSpaBooking, updateSpaBooking, adminCreateSpaBooking, fetchAdminMassageBookings, updateMassageBooking, cancelMassageBooking, deleteMassageBooking, adminCreateMassageBooking } from "../lib/api";
import { LABELS, ALLERGENS, INTOLERANCES, tr } from "../lib/i18n";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../hooks/use-toast";
import LoyaltyEditor from "../components/admin/LoyaltyEditor";
import { useSlotBlocks, FreeSlotCell, BlockedSlotCell, ClosePeriodButton, HorseClosures } from "../components/admin/SlotBlocks";
import StatsEditor from "../components/admin/StatsEditor";
import SecurityEditor from "../components/admin/SecurityEditor";
import { MailCleanupPanel } from "../components/admin/mail/MailCleanupPanel";
import RoomsEditor from "../components/admin/RoomsEditor";
import ExperiencesPanel from "../components/admin/ExperiencesPanel";
import PoisEditor from "../components/admin/PoisEditor";
import LocksEditor from "../components/admin/LocksEditor";
import RestaurantAgenda from "../components/admin/RestaurantAgenda";
import ReviewsEditor from "../components/admin/ReviewsEditor";
import ExternalReviewsEditor from "../components/admin/ExternalReviewsEditor";
import HousekeepingWidget from "../components/admin/HousekeepingWidget";
import { RemindersBell } from "../components/admin/reminders/RemindersBell";
import { CassaDishBox, pushDishToCassa } from "../components/admin/CassaDishBox";
import { BreakfastPanel } from "../components/admin/BreakfastPanel";
import MessagesEditor from "../components/admin/MessagesEditor";
import TakeawayEditor from "../components/admin/TakeawayEditor";
import ShopEditor from "../components/admin/ShopEditor";
import OrderNotifBanner from "../components/admin/OrderNotifBanner";
import NotificationsBell from "../components/admin/NotificationsBell";
import { PushPromptBanner } from "../components/admin/PushPromptBanner";
import { StaffAppsBar } from "../components/admin/StaffAppsBar";
import { QuotesPanel } from "../components/admin/quotes/QuotesPanel";
import { PayRequestsPanel } from "../components/admin/PayRequestsPanel";
import SocialStudioEditor from "./admin/SocialStudioEditor";
import StaffPanel from "../components/admin/StaffPanel";
import GiftsEditor from "../components/admin/GiftsEditor";
import MusicPanel from "../components/admin/music/MusicPanel";
import { MultiLangInput } from "../components/admin/common";
import { CustomerAutocomplete } from "../components/CustomerAutocomplete";
import { waAnchorProps, openWhatsApp, getWaMode } from "../lib/whatsapp";
import { WaOpenModeBox as WaOpenModeBoxShared } from "../components/WaOpenModeBox";
import axios from "axios";

const searchAdminCustomers = (q) => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/customers/search`, { params: { q }, headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }).then((r) => r.data);

function WaOpenModeBox() {
  return <WaOpenModeBoxShared className="mb-4" />;
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      if (hasToken()) {
        const ok = await verifyToken();
        setAuthed(ok);
        if (!ok) logout();
      }
      setChecking(false);
    })();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7ebdf]">
        <Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" />
      </div>
    );
  }
  if (!authed) return <LoginScreen onSuccess={() => setAuthed(true)} />;
  return <AdminDashboard onLogout={() => { logout(); setAuthed(false); }} />;
}

function LoginScreen({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await login(password);
      onSuccess();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Password errata");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#4a1c17] px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#f7ebdf] rounded-2xl shadow-xl p-8">
        <div className="mx-auto h-14 w-14 rounded-full bg-[#4a1c17] text-[#f7ebdf] flex items-center justify-center">
          <Lock className="h-6 w-6" />
        </div>
        <h1 className="mt-4 font-serif text-2xl font-bold text-[#2b1410] text-center">Admin — Il Pozzo dei Giganti</h1>
        <p className="mt-1 text-sm text-[#7c5a4e] text-center">Accedi per gestire il menu</p>

        <label className="block mt-6 text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          className="mt-1 w-full rounded-lg border border-[#e8dcc8] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/30"
        />
        {err && <p className="mt-2 text-xs text-red-700">{err}</p>}

        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] py-2.5 font-medium disabled:opacity-60"
        >
          {loading ? "Accesso..." : "Accedi"}
        </button>
      </form>
    </div>
  );
}

function ToggleSwitch({ checked, onChange, disabled = false, title, size = "sm" }) {
  const sizes = {
    sm: { track: "h-5 w-9",  knob: "h-3.5 w-3.5", on: "translate-x-5", off: "translate-x-1" },
    lg: { track: "h-7 w-12", knob: "h-5 w-5",     on: "translate-x-6", off: "translate-x-1" },
  };
  const s = sizes[size] || sizes.sm;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      title={title}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className={`relative inline-flex ${s.track} shrink-0 items-center rounded-full transition-colors duration-150 border active:scale-95 ${
        checked ? "bg-emerald-500 border-emerald-500" : "bg-neutral-300 border-neutral-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span className={`inline-block ${s.knob} transform rounded-full bg-white shadow transition-transform duration-150 ${checked ? s.on : s.off}`} />
    </button>
  );
}


function CollapsibleSection({ title, subtitle, initiallyOpen, badge = 0, badgeTitle = "", children }) {
  const [open, setOpen] = useState(!!initiallyOpen);
  useEffect(() => { if (badge > 0) setOpen(true); }, [badge]);
  return (
    <section className="mt-8 rounded-2xl border border-[#e8dcc8] bg-white/60 overflow-hidden" data-testid={`collapsible-${(title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#faf3ee]"
      >
        <div className="min-w-0">
          <h3 className="font-serif text-lg text-[#2b1410] flex items-center gap-2">{title}
            {badge > 0 && <span className="inline-flex items-center rounded-full bg-red-600 text-white text-[11px] font-bold px-2 py-0.5 font-sans" data-testid={`section-badge-${(title || "").toLowerCase()}`}>{badge} {badgeTitle}</span>}
          </h3>
          {subtitle && <p className="text-xs text-[#7c5a4e] mt-0.5">{subtitle}</p>}
        </div>
        <ChevronRight className={`h-5 w-5 text-[#4a1c17] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-[#f3e6d5]">{children}</div>}
    </section>
  );
}

function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState(() => { const t = new URLSearchParams(window.location.search).get("tab") || "menu"; return t === "music" ? "ambienti" : t; });
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingPins, setPendingPins] = useState(0);
  const [pendingHorse, setPendingHorse] = useState(0);

  const refresh = async () => {
    const [r, m, ms] = await Promise.all([fetchRestaurant(), fetchMenu(true), fetchMenus(true)]);
    setRestaurant(r);
    // also fetch the flat menu view including hidden - we need include_hidden
    setMenu(m);
    setMenus(ms);
  };

  useEffect(() => {
    (async () => {
      try { await refresh(); } finally { setLoading(false); }
    })();
  }, []);

  // Poll pending-pins count for the Serrature tab badge (arrivi oggi/domani senza PIN).
  useEffect(() => {
    const tok = localStorage.getItem("admin_token");
    if (!tok) return;
    let stop = false;
    const fetchCount = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/hk/reservations/pending-pins`,
          { headers: { Authorization: `Bearer ${tok}` } },
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!stop) setPendingPins(data.count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { stop = true; clearInterval(t); };
  }, [tab]);

  // Poll pending horse-ride bookings (status=pending)
  useEffect(() => {
    const tok = localStorage.getItem("admin_token");
    if (!tok) return;
    let stop = false;
    const fetchHorse = async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/horse/bookings?from_date=${today}`,
          { headers: { Authorization: `Bearer ${tok}` } },
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!stop) setPendingHorse((data.bookings || []).filter((b) => b.status === "pending").length);
      } catch { /* silent */ }
    };
    fetchHorse();
    const t = setInterval(fetchHorse, 60000);
    return () => { stop = true; clearInterval(t); };
  }, [tab]);

  return (
    <div className="min-h-screen bg-[#f7ebdf]">
      <OrderNotifBanner />
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#4a1c17] text-[#f7ebdf] shadow pt-[env(safe-area-inset-top)]">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-[#f7ebdf] text-[#4a1c17] flex items-center justify-center font-serif font-bold shrink-0">P</div>
            <div className="min-w-0">
              <div className="font-serif text-sm sm:text-lg font-semibold tracking-wide leading-tight truncate">Il Pozzo dei Giganti</div>
              <div className="hidden sm:block text-xs opacity-70 mt-0.5">Pannello Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <RemindersBell />
            <NotificationsBell />
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#f7ebdf] text-[#4a1c17] px-2.5 sm:px-3 py-1.5 text-sm font-medium hover:bg-white transition-colors"
              title="Apre il menu in una nuova finestra come lo vedono i clienti"
              aria-label="Vedi come cliente"
            >
              <Eye className="h-4 w-4" /> <span className="hidden sm:inline">Vedi come cliente</span>
            </a>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1 rounded-full border border-[#f7ebdf]/30 px-2.5 sm:px-3 py-1.5 text-sm hover:bg-[#f7ebdf]/10"
              aria-label="Logout"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
        <nav
          className="max-w-6xl mx-auto flex gap-1 pb-2 px-3 sm:px-4 overflow-x-auto admin-tab-scroll"
          data-testid="admin-nav"
        >
          <TabButton icon={<Utensils className="h-4 w-4" />} label="Menu" shortLabel="Menu" active={tab === "menu"} onClick={() => setTab("menu")} />
          <TabButton icon={<HomeIcon className="h-4 w-4" />} label="Camere" shortLabel="Camere" active={tab === "rooms"} onClick={() => setTab("rooms")} badge={pendingPins} />
          <TabButton icon={<CalendarDays className="h-4 w-4" />} label="Prenotazioni" shortLabel="Preno" active={tab === "bookings"} onClick={() => setTab("bookings")} badge={pendingHorse} />
          <TabButton icon={<Heart className="h-4 w-4" />} label="Fidelizzazione" shortLabel="Fideliz" active={tab === "loyalty"} onClick={() => setTab("loyalty")} />
          <TabButton icon={<Compass className="h-4 w-4" />} label="Dintorni" shortLabel="Dintorni" active={tab === "pois"} onClick={() => setTab("pois")} />
          <TabButton icon={<Sparkles className="h-4 w-4" />} label="Social" shortLabel="Social" active={tab === "social"} onClick={() => setTab("social")} />
          <TabButton icon={<StoreIcon className="h-4 w-4" />} label="Shop" shortLabel="Shop" active={tab === "shop"} onClick={() => setTab("shop")} />
          <TabButton icon={<Gift className="h-4 w-4" />} label="Regali" shortLabel="Regali" active={tab === "gifts"} onClick={() => setTab("gifts")} />
          <TabButton icon={<Radio className="h-4 w-4" />} label="Ambienti" shortLabel="Ambienti" active={tab === "ambienti"} onClick={() => setTab("ambienti")} />
          <TabButton icon={<Users className="h-4 w-4" />} label="Team" shortLabel="Team" active={tab === "staff"} onClick={() => setTab("staff")} />
          <TabButton icon={<KeyRound className="h-4 w-4" />} label="Impostazioni" shortLabel="Imposta" active={tab === "config"} onClick={() => setTab("config")} />
        </nav>
      </header>
      <PushPromptBanner />

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <HousekeepingWidget />
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>
        ) : tab === "menu" ? (
          <>
            <MenuEditor menu={menu} menus={menus} onChange={refresh} />
            <CollapsibleSection title="Asporto" subtitle="Ordini takeaway & ritiro in struttura" initiallyOpen={false}>
              <TakeawayEditor />
            </CollapsibleSection>
            <CollapsibleSection title="Statistiche" subtitle="Visualizzazioni menu, top piatti, lingue clienti" initiallyOpen={false}>
              <StatsEditor />
            </CollapsibleSection>
            <CollapsibleSection title="Impostazioni Ristorante" subtitle="Nome, orari, contatti — modifica una volta ogni morte di papa" initiallyOpen={false}>
              <RestaurantEditor restaurant={restaurant} onChange={refresh} />
            </CollapsibleSection>
          </>
        ) : tab === "bookings" ? (
          <>
            <StaffAppsBar />
            <CollapsibleSection title="Preventivi" subtitle="Soggiorni ed eventi · link, PDF, accettazione online e caparra" initiallyOpen={false}>
              <QuotesPanel />
            </CollapsibleSection>
            <CollapsibleSection title="Richieste di pagamento" subtitle="Link per pagare con carta · pernottamenti, servizi, saldi" initiallyOpen={false}>
              <PayRequestsPanel />
            </CollapsibleSection>
            <CollapsibleSection title="Ristorante" subtitle="Agenda tavoli · pranzo e cena · appuntamenti ed eventi" initiallyOpen={true}>
              <RestaurantAgenda />
            </CollapsibleSection>
            <CollapsibleSection title="Spa" subtitle="Bagni turchi & sauna" initiallyOpen={false}>
              <SpaBookingsEditor />
            </CollapsibleSection>
            <CollapsibleSection title="Massaggi" subtitle="Trattamenti benessere" initiallyOpen={false}>
              <MassageBookingsEditor />
            </CollapsibleSection>
            <CollapsibleSection title="Cavallo" subtitle="Cavalcate al tramonto — con notifica esterna a Riccardo" initiallyOpen={false}>
              <HorseBookingsEditor />
            </CollapsibleSection>
            <CollapsibleSection title="Eventi" subtitle="Calendario esperienze & sagre" initiallyOpen={false}>
              <ExperiencesPanel />
            </CollapsibleSection>
          </>
        ) : tab === "rooms" ? (
          <>
            <CollapsibleSection title="Colazione al bar" subtitle="Voucher WhatsApp per la colazione al Bar del Borgo · conteggio mensile" initiallyOpen={false}>
              <BreakfastPanel />
            </CollapsibleSection>
            <CollapsibleSection title="Pulizie" subtitle="Checklist camere, frigobar, presenze staff" initiallyOpen={true}>
              <div className="rounded-xl bg-[#faf3ee] border border-[#e8dcc8] p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
                <div>
                  <div className="text-sm text-[#2b1410] font-medium">Gestione completa pulizie</div>
                  <div className="text-xs text-[#7c5a4e] mt-0.5">
                    La checklist giornaliera vive in una pagina dedicata per lo staff. Clicca per aprirla.
                  </div>
                </div>
                <button
                  onClick={() => navigate("/admin/pulizie/oggi")}
                  className="inline-flex items-center gap-2 rounded-full bg-[#4a1c17] text-white px-4 py-2 text-sm font-medium hover:bg-[#6a2c22]"
                  data-testid="open-pulizie-btn"
                >
                  <ClipboardList className="h-4 w-4" />
                  Apri pulizie
                </button>
              </div>
            </CollapsibleSection>
            <CollapsibleSection title="Serrature" subtitle="TTLock · PIN camere · storico anti-manomissione" initiallyOpen={false} badge={pendingPins} badgeTitle={pendingPins === 1 ? "arrivo senza PIN" : "arrivi senza PIN"}>
              <LocksEditor />
            </CollapsibleSection>
            <CollapsibleSection title="Domotica" subtitle="Luci, tapparelle, saune (SmartThings + Tuya)" initiallyOpen={new URLSearchParams(window.location.search).has("st")}>
              <RoomsEditor />
            </CollapsibleSection>
          </>
        ) : tab === "pois" ? (
          <PoisEditor />
        ) : tab === "loyalty" ? (
          <LoyaltyEditor />
        ) : tab === "shop" ? (
          <ShopEditor />
        ) : tab === "gifts" ? (
          <GiftsEditor />
        ) : tab === "ambienti" ? (
          <MusicPanel />
        ) : tab === "social" ? (
          <SocialStudioEditor />
        ) : tab === "staff" ? (
          <StaffPanel />
        ) : tab === "config" ? (
          <>
            <CollapsibleSection title="Recensioni esterne" subtitle="Google · TripAdvisor · TheFork · Booking — punteggi e link visibili sul menu pubblico" initiallyOpen={true}>
              <ExternalReviewsEditor />
            </CollapsibleSection>
            <CollapsibleSection title="Messaggi WhatsApp" subtitle="Modelli di risposta automatica (Ospite / Interno)" initiallyOpen={false}>
              <WaOpenModeBox />
              <MessagesEditor />
            </CollapsibleSection>
            <CollapsibleSection title="Sicurezza" subtitle="Password admin, PIN pulizie" initiallyOpen={false}>
              <SecurityEditor />
            </CollapsibleSection>
            <CollapsibleSection title="Posta" subtitle="Casella ilpozzodeigiganti@gmail.com — chi aspetta risposta, pubblicità da bloccare, archiviazione per mittente" initiallyOpen={false}>
              <MailCleanupPanel />
            </CollapsibleSection>
          </>
        ) : (
          <SecurityEditor />
        )}
      </main>
      <Toaster />
    </div>
  );
}

function TabButton({ icon, label, shortLabel, active, onClick, badge = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (active && ref.current) {
      // Bring the active tab into view on switch (mobile-friendly).
      try { ref.current.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" }); } catch (_) {}
    }
  }, [active]);
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={`relative inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded-t-lg text-xs sm:text-sm font-medium transition-colors shrink-0 whitespace-nowrap ${
        active ? "bg-[#f7ebdf] text-[#4a1c17]" : "text-[#f7ebdf]/80 hover:bg-[#f7ebdf]/10"
      }`}
    >
      {icon}
      <span className="sm:hidden">{shortLabel || label}</span>
      <span className="hidden sm:inline">{label}</span>
      {badge > 0 && (
        <span
          data-testid={`tab-badge-${(shortLabel || label).toLowerCase()}`}
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center shadow-md ring-2 ring-[#4a1c17] animate-pulse"
          title={`${badge} ospiti oggi/domani senza PIN`}
        >
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </button>
  );
}

// ---------------- Menu editor ----------------
function MenuEditor({ menu, menus, onChange }) {
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [creatingCat, setCreatingCat] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!selectedMenuId && menus && menus.length > 0) {
      setSelectedMenuId(menus[0].id);
    }
  }, [menus, selectedMenuId]);

  const currentMenu = menus.find((m) => m.id === selectedMenuId);
  const categoriesInMenu = menu.filter((c) => c.menuId === selectedMenuId);
  const onMoveCategory = async (from, to) => {
    const cats = [...categoriesInMenu];
    const [moved] = cats.splice(from, 1);
    cats.splice(to, 0, moved);
    try {
      await reorderCategories(cats.map((c, i) => ({ id: c.id, order: i })));
      await onChange();
    } catch (e) {
      toast({ title: "Errore riordino", description: e.message });
    }
  };

  const addCategory = async () => {
    if (!selectedMenuId) return;
    setCreatingCat(true);
    try {
      const nextOrder = (categoriesInMenu[categoriesInMenu.length - 1]?.order || 0) + 1;
      await createCategory({
        menuId: selectedMenuId,
        name: { it: "Nuova categoria", en: "New category", es: "Nueva", fr: "Nouvelle", de: "Neu" },
        subtitle: { it: "", en: "", es: "", fr: "", de: "" },
        order: nextOrder,
      });
      toast({ title: "Categoria creata" });
      await onChange();
    } catch (e) {
      toast({ title: "Errore", description: e.message });
    } finally { setCreatingCat(false); }
  };



  const [translatingAll, setTranslatingAll] = useState(false);
  const [transJob, setTransJob] = useState(null); // {done, total, updated, errors, status}
  const translateAll = async () => {
    if (!window.confirm("Tradurre TUTTI i piatti in EN/ES/FR/DE partendo dall'italiano? L'operazione dura alcuni minuti - puoi lasciare la finestra aperta.")) return;
    setTranslatingAll(true);
    setTransJob({ status: "running", done: 0, total: 0, updated: 0, errors: 0 });
    try {
      const start = await startBulkTranslate();
      const jobId = start.job_id;
      // Poll every 2 seconds
      const poll = async () => {
        try {
          const st = await getTranslateJob(jobId);
          setTransJob(st);
          if (st.status === "completed" || st.status === "failed") {
            setTranslatingAll(false);
            if (st.status === "completed") {
              toast({ title: `Traduzione completata`, description: `${st.updated}/${st.total} piatti tradotti${st.errors ? ` · ${st.errors} errori` : ""}` });
              await onChange();
            } else {
              toast({ title: "Errore traduzione", description: st.message || "Sconosciuto" });
            }
            return;
          }
          setTimeout(poll, 2000);
        } catch (e) {
          setTranslatingAll(false);
          toast({ title: "Errore polling", description: e?.response?.data?.detail || e.message });
        }
      };
      setTimeout(poll, 1500);
    } catch (e) {
      setTranslatingAll(false);
      toast({ title: "Errore avvio traduzione", description: e?.response?.data?.detail || e.message });
    }
  };

  // -------- Allergen AI bulk --------
  const [allergJob, setAllergJob] = useState(null); // {done,total,results,status}
  const [allergOpen, setAllergOpen] = useState(false);
  const [allergCategoryId, setAllergCategoryId] = useState(""); // "" = tutto il menu
  // Selections stored as { [dishId]: { allergens, may_contain, intolerances } }
  const [allergSelections, setAllergSelections] = useState({});
  const [resettingAllerg, setResettingAllerg] = useState(false);

  const startAllergBulk = async () => {
    if (!selectedMenuId) {
      toast({ title: "Seleziona prima un menu" });
      return;
    }
    const catLabel = allergCategoryId
      ? (categoriesInMenu.find((c) => c.id === allergCategoryId)?.name?.it || "categoria")
      : (currentMenu ? tr(currentMenu.name, "it") : "menu");
    if (!window.confirm(`Analizzare con l'AI i piatti di "${catLabel}"? Riceverai un'anteprima da confermare — nulla verrà salvato prima della tua approvazione.`)) return;
    setAllergJob({ status: "running", done: 0, total: 0, results: [] });
    setAllergOpen(true);
    try {
      const tok = localStorage.getItem("admin_token");
      const params = new URLSearchParams();
      if (allergCategoryId) params.set("category_id", allergCategoryId);
      else params.set("menu_id", selectedMenuId);
      const startR = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dishes/suggest-allergens-bulk?${params}`, {
        method: "POST", headers: { Authorization: `Bearer ${tok}` },
      });
      const { job_id } = await startR.json();
      const poll = async () => {
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dishes/suggest-allergens-bulk/${job_id}`, {
            headers: { Authorization: `Bearer ${tok}` },
          });
          const st = await r.json();
          setAllergJob(st);
          if (st.status === "done" || st.status === "error") {
            const sel = {};
            (st.results || []).forEach((row) => {
              sel[row.dish_id] = {
                allergens: row.suggested_allergens || [],
                may_contain: row.suggested_may_contain || [],
                intolerances: row.suggested_intolerances || [],
              };
            });
            setAllergSelections(sel);
            return;
          }
          setTimeout(poll, 2000);
        } catch (e) {
          toast({ title: "Errore polling", description: e.message });
        }
      };
      setTimeout(poll, 1500);
    } catch (e) {
      toast({ title: "Errore", description: e?.message });
      setAllergJob(null);
    }
  };

  const resetAllergens = async () => {
    if (!selectedMenuId) {
      toast({ title: "Seleziona prima un menu" });
      return;
    }
    const scope = allergCategoryId
      ? `categoria "${categoriesInMenu.find((c) => c.id === allergCategoryId)?.name?.it || "?"}"`
      : `menu "${currentMenu ? tr(currentMenu.name, "it") : ""}"`;
    if (!window.confirm(`⚠️ Azzerare Contiene, Tracce e Intolleranze su TUTTI i piatti della ${scope}? Operazione non reversibile.`)) return;
    setResettingAllerg(true);
    try {
      const tok = localStorage.getItem("admin_token");
      const params = new URLSearchParams();
      if (allergCategoryId) params.set("category_id", allergCategoryId);
      else params.set("menu_id", selectedMenuId);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dishes/reset-allergens?${params}`, {
        method: "POST", headers: { Authorization: `Bearer ${tok}` },
      });
      const data = await r.json();
      toast({ title: "Azzerato", description: `${data.cleared} piatti aggiornati` });
      await onChange();
    } catch (e) {
      toast({ title: "Errore", description: e?.message });
    } finally {
      setResettingAllerg(false);
    }
  };

  const applyAllergSelected = async (onlyChanged = false) => {
    const items = (allergJob?.results || [])
      .map((r) => {
        const s = allergSelections[r.dish_id] || { allergens: [], may_contain: [], intolerances: [] };
        return {
          dish_id: r.dish_id,
          allergens: s.allergens,
          may_contain: s.may_contain,
          intolerances: s.intolerances,
          _cur_all: r.current_allergens || [],
          _cur_may: r.current_may_contain || [],
          _cur_int: r.current_intolerances || [],
        };
      })
      .filter((it) => {
        if (!onlyChanged) return true;
        const sameAll = JSON.stringify([...it.allergens].sort()) === JSON.stringify([...it._cur_all].sort());
        const sameMay = JSON.stringify([...it.may_contain].sort()) === JSON.stringify([...it._cur_may].sort());
        const sameInt = JSON.stringify([...it.intolerances].sort()) === JSON.stringify([...it._cur_int].sort());
        return !(sameAll && sameMay && sameInt);
      })
      .map(({ _cur_all, _cur_may, _cur_int, ...rest }) => rest);
    if (items.length === 0) {
      toast({ title: "Nessuna modifica da applicare" });
      return;
    }
    if (!window.confirm(`Applicare a ${items.length} piatti? Verranno aggiornati Contiene, Tracce e Intolleranze.`)) return;
    try {
      const tok = localStorage.getItem("admin_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dishes/apply-allergens`, {
        method: "POST", headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      const data = await r.json();
      toast({ title: "Salvato", description: `${data.applied} piatti aggiornati` });
      setAllergOpen(false);
      setAllergJob(null);
      await onChange();
    } catch (e) {
      toast({ title: "Errore salvataggio", description: e.message });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-serif text-2xl font-bold text-[#2b1410]">Gestione Menu</h2>
        <div className="flex gap-2 flex-wrap">
          <button onClick={translateAll} disabled={translatingAll || menu.length === 0} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#4a1c17] text-[#4a1c17] hover:bg-[#4a1c17] hover:text-[#f7ebdf] px-3.5 py-2 text-sm font-medium disabled:opacity-50">
            {translatingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
            {translatingAll ? "Traducendo…" : "Traduci tutto il menu"}
          </button>
          <select
            value={allergCategoryId}
            onChange={(e) => setAllergCategoryId(e.target.value)}
            disabled={!selectedMenuId || allergJob?.status === "running" || resettingAllerg}
            className="rounded-full bg-white border border-amber-600 text-amber-700 px-3 py-2 text-sm font-medium disabled:opacity-50"
            data-testid="allergen-category-select"
          >
            <option value="">Tutto il menu</option>
            {categoriesInMenu.map((c) => (
              <option key={c.id} value={c.id}>{c.name?.it || "—"}</option>
            ))}
          </select>
          <button onClick={startAllergBulk} disabled={allergJob?.status === "running" || !selectedMenuId} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white px-3.5 py-2 text-sm font-medium disabled:opacity-50" data-testid="menu-allergen-ai-btn">
            {allergJob?.status === "running" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {allergJob?.status === "running" ? `Analisi… ${allergJob.done}/${allergJob.total || "?"}` : "AI Allergeni"}
          </button>
          <button onClick={resetAllergens} disabled={resettingAllerg || !selectedMenuId} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-500 text-red-600 hover:bg-red-500 hover:text-white px-3.5 py-2 text-sm font-medium disabled:opacity-50" data-testid="menu-allergen-reset-btn">
            {resettingAllerg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {resettingAllerg ? "Azzeramento…" : "Reset"}
          </button>
        </div>
      </div>

      {transJob && translatingAll && (
        <div className="bg-white border border-[#e8dcc8] rounded-xl p-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium text-[#2b1410]">Traduzione in corso…</span>
            <span className="text-[#7c5a4e] tabular-nums">
              {transJob.done || 0} / {transJob.total || "?"} piatti
              {transJob.errors ? <span className="text-red-600 ml-2">· {transJob.errors} errori</span> : null}
            </span>
          </div>
          <div className="h-2 rounded-full bg-[#faf3ee] overflow-hidden">
            <div
              className="h-full bg-[#4a1c17] transition-all duration-500"
              style={{ width: `${transJob.total ? Math.round((transJob.done / transJob.total) * 100) : 5}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-[#7c5a4e]">Puoi continuare a lavorare in un'altra tab. La traduzione va avanti in background.</p>
        </div>
      )}

      {/* Menu manager: list of top-level menus */}
      <MenuListEditor menus={menus} selectedMenuId={selectedMenuId} onSelect={setSelectedMenuId} onChange={onChange} />

      {allergOpen && (
        <AllergenBulkModal
          job={allergJob}
          selections={allergSelections}
          onSelectionsChange={setAllergSelections}
          onApplyAll={() => applyAllergSelected(false)}
          onApplyChanged={() => applyAllergSelected(true)}
          onClose={() => { setAllergOpen(false); setAllergJob(null); }}
        />
      )}

      {/* Categories inside the selected menu */}
      {currentMenu && (
        <div className="bg-[#faf3ee] border border-[#e8dcc8] rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase font-semibold text-[#7c5a4e]">Categorie di</div>
              <div className="font-serif text-lg font-semibold text-[#2b1410]">{tr(currentMenu.name, "it")}</div>
            </div>
            <button onClick={addCategory} disabled={creatingCat} className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2 text-sm font-medium disabled:opacity-60">
              <Plus className="h-4 w-4" /> Nuova categoria
            </button>
          </div>
          {categoriesInMenu.length === 0 ? (
            <div className="p-6 rounded-lg border-2 border-dashed border-[#e8dcc8] bg-white text-center text-sm text-[#7c5a4e]">
              Nessuna categoria in questo menu. Aggiungine una per iniziare.
            </div>
          ) : (
            <div className="space-y-3">
              {categoriesInMenu.map((cat, idx) => (
                <CategoryBlock
                  key={cat.id}
                  category={cat}
                  expanded={expanded[cat.id]}
                  onToggle={() => setExpanded((s) => ({ ...s, [cat.id]: !s[cat.id] }))}
                  onChange={onChange}
                  onMoveUp={idx > 0 ? () => onMoveCategory(idx, idx - 1) : null}
                  onMoveDown={idx < categoriesInMenu.length - 1 ? () => onMoveCategory(idx, idx + 1) : null}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AllergenBulkModal({ job, selections, onSelectionsChange, onApplyAll, onApplyChanged, onClose }) {
  const running = job?.status === "running";
  const done = job?.status === "done";
  const rows = job?.results || [];

  const selOf = (dishId) => selections[dishId] || { allergens: [], may_contain: [], intolerances: [] };

  const toggleCode = (dishId, group, code) => {
    const cur = selOf(dishId);
    const arr = new Set(cur[group] || []);
    if (arr.has(code)) arr.delete(code); else arr.add(code);
    onSelectionsChange({ ...selections, [dishId]: { ...cur, [group]: Array.from(arr).sort() } });
  };

  const applySuggested = (row) => {
    onSelectionsChange({
      ...selections,
      [row.dish_id]: {
        allergens: [...(row.suggested_allergens || [])],
        may_contain: [...(row.suggested_may_contain || [])],
        intolerances: [...(row.suggested_intolerances || [])],
      },
    });
  };
  const applyCurrent = (row) => {
    onSelectionsChange({
      ...selections,
      [row.dish_id]: {
        allergens: [...(row.current_allergens || [])],
        may_contain: [...(row.current_may_contain || [])],
        intolerances: [...(row.current_intolerances || [])],
      },
    });
  };

  const GroupPicker = ({ title, catalog, selected, onToggle }) => (
    <div>
      <div className="text-[10px] uppercase font-semibold text-[#7c5a4e] mb-1">{title}</div>
      <div className="flex flex-wrap gap-1">
        {Object.keys(catalog).map((code) => {
          const active = selected.includes(code);
          return (
            <button
              key={code}
              onClick={() => onToggle(code)}
              className={`px-1.5 py-0.5 rounded-full text-[10px] border ${active ? "bg-[#4a1c17] text-white border-[#4a1c17]" : "bg-white text-[#7c5a4e] border-[#e8dcc8] hover:bg-[#faf3ee]"}`}
            >
              {tr(catalog[code], "it")}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-6" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <header className="px-5 py-4 border-b border-[#e8dcc8] flex items-center justify-between bg-[#faf3ee]">
          <div>
            <h3 className="font-serif text-xl text-[#2b1410]">AI Sicurezza Alimentare — Anteprima</h3>
            <p className="text-xs text-[#7c5a4e] mt-0.5">Contiene · Tracce · Intolleranze. Rivedi prima di applicare.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#7c5a4e] tabular-nums">{job?.done || 0} / {job?.total || "?"}</span>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white" data-testid="allerg-modal-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        {running && (
          <div className="p-4">
            <div className="h-2 rounded-full bg-[#faf3ee] overflow-hidden">
              <div className="h-full bg-amber-600 transition-all duration-500" style={{ width: `${job.total ? Math.round((job.done / job.total) * 100) : 5}%` }} />
            </div>
            <p className="mt-3 text-sm text-[#7c5a4e]">Analisi in corso… puoi già rivedere i piatti già processati.</p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto divide-y divide-[#f3e6d5]">
          {rows.length === 0 && !running && (
            <div className="p-10 text-center text-sm text-[#7c5a4e]">Nessun risultato ancora disponibile.</div>
          )}
          {rows.map((r) => {
            const sel = selOf(r.dish_id);
            const curTag = (list) => list.length === 0 ? <span className="text-[10px] text-[#7c5a4e] italic">nessuno</span> :
              list.map((c) => <span key={c} className="px-1 py-0.5 rounded bg-white border border-[#e8dcc8] text-[9px] text-[#4a1c17] mr-1">{tr((ALLERGENS[c] || INTOLERANCES[c]) || { it: c }, "it")}</span>);
            const sugTag = (list) => list.length === 0 ? <span className="text-[10px] text-[#7c5a4e] italic">nessuno</span> :
              list.map((c) => <span key={c} className="px-1 py-0.5 rounded bg-amber-100 border border-amber-300 text-[9px] text-amber-800 mr-1">{tr((ALLERGENS[c] || INTOLERANCES[c]) || { it: c }, "it")}</span>);
            return (
              <div key={r.dish_id} className="p-3">
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div>
                    <div className="font-medium text-[#2b1410] text-sm">{r.name}</div>
                    <div className="text-[11px] text-[#7c5a4e] mt-0.5 italic">💡 {r.reasoning}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => applyCurrent(r)} className="text-[10px] text-[#7c5a4e] underline whitespace-nowrap">Usa attuali</button>
                    <button onClick={() => applySuggested(r)} className="text-[10px] text-amber-700 underline whitespace-nowrap">Usa AI</button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <div className="rounded-lg bg-[#faf3ee] p-2">
                    <div className="text-[10px] uppercase font-semibold text-[#7c5a4e] mb-1">Attuali</div>
                    <div><b>Contiene:</b> {curTag(r.current_allergens || [])}</div>
                    <div className="mt-1"><b>Tracce:</b> {curTag(r.current_may_contain || [])}</div>
                    <div className="mt-1"><b>Intoll.:</b> {curTag(r.current_intolerances || [])}</div>
                  </div>
                  <div className="rounded-lg bg-amber-50/50 p-2">
                    <div className="text-[10px] uppercase font-semibold text-amber-800 mb-1">Suggeriti AI</div>
                    <div><b>Contiene:</b> {sugTag(r.suggested_allergens || [])}</div>
                    <div className="mt-1"><b>Tracce:</b> {sugTag(r.suggested_may_contain || [])}</div>
                    <div className="mt-1"><b>Intoll.:</b> {sugTag(r.suggested_intolerances || [])}</div>
                  </div>
                  <div className="rounded-lg bg-white border border-[#e8dcc8] p-2 space-y-2">
                    <GroupPicker title="Contiene" catalog={ALLERGENS} selected={sel.allergens} onToggle={(c) => toggleCode(r.dish_id, "allergens", c)} />
                    <GroupPicker title="Può contenere tracce" catalog={ALLERGENS} selected={sel.may_contain} onToggle={(c) => toggleCode(r.dish_id, "may_contain", c)} />
                    <GroupPicker title="Intolleranze" catalog={INTOLERANCES} selected={sel.intolerances} onToggle={(c) => toggleCode(r.dish_id, "intolerances", c)} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <footer className="px-5 py-3 border-t border-[#e8dcc8] bg-[#faf3ee] flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-[#7c5a4e]">{done ? `Analisi completa: ${rows.length} piatti` : "Analisi in corso…"}</div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-full border border-[#4a1c17] text-[#4a1c17]">Chiudi</button>
            <button onClick={onApplyChanged} disabled={!done} className="px-3 py-1.5 text-sm rounded-full bg-amber-600 text-white disabled:opacity-50" data-testid="allerg-apply-changed">
              Applica solo le modifiche
            </button>
            <button onClick={onApplyAll} disabled={!done} className="px-3 py-1.5 text-sm rounded-full bg-[#4a1c17] text-white disabled:opacity-50" data-testid="allerg-apply-all">
              Applica a tutti ({rows.length})
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function MenuListEditor({ menus, selectedMenuId, onSelect, onChange }) {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= menus.length) return;
    // swap orders
    const a = menus[idx], b = menus[target];
    try {
      await reorderMenus([
        { id: a.id, order: b.order },
        { id: b.id, order: a.order },
      ]);
      await onChange();
    } catch (e) { toast({ title: "Errore", description: e.message }); }
  };

  const addMenu = async () => {
    setCreating(true);
    try {
      const nextOrder = ((menus[menus.length - 1]?.order) || 0) + 1;
      const created = await createMenu({
        name: { it: "Nuovo menu", en: "New menu", es: "Nuevo menú", fr: "Nouveau menu", de: "Neues Menü" },
        subtitle: { it:"",en:"",es:"",fr:"",de:"" },
        icon: "utensils",
        order: nextOrder,
      });
      toast({ title: "Menu creato" });
      await onChange();
      onSelect(created.id);
      setEditingId(created.id);
    } catch (e) {
      toast({ title: "Errore", description: e.message });
    } finally { setCreating(false); }
  };

  const removeMenu = async (m) => {
    if (!window.confirm(`Eliminare il menu "${tr(m.name, "it")}" e tutte le sue categorie/piatti?`)) return;
    try {
      await deleteMenu(m.id);
      toast({ title: "Menu eliminato" });
      await onChange();
      if (selectedMenuId === m.id) onSelect(null);
    } catch (e) { toast({ title: "Errore", description: e.message }); }
  };

  return (
    <div className="bg-white border border-[#e8dcc8] rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-[#4a1c17]" />
          <span className="font-serif text-lg font-semibold text-[#2b1410]">Menu principali</span>
          <span className="text-xs text-[#7c5a4e]">(usa le frecce per riordinare)</span>
        </div>
        <button onClick={addMenu} disabled={creating} className="inline-flex items-center gap-1.5 rounded-full border border-[#4a1c17] text-[#4a1c17] hover:bg-[#4a1c17] hover:text-[#f7ebdf] px-3 py-1.5 text-sm font-medium disabled:opacity-60">
          <Plus className="h-4 w-4" /> Nuovo menu
        </button>
      </div>
      <div className="space-y-2">
        {menus.map((m, idx) => {
          const isVisible = m.visible !== false;
          const toggleVisible = async () => {
            try {
              await updateMenu(m.id, { visible: !isVisible });
              toast({ title: isVisible ? "Menu nascosto" : "Menu visibile" });
              await onChange();
            } catch (e) { toast({ title: "Errore", description: e.message }); }
          };
          return (
          <div key={m.id} className={`rounded-lg border ${selectedMenuId === m.id ? "border-[#4a1c17] bg-[#4a1c17] text-[#f7ebdf]" : "border-[#e8dcc8] bg-white text-[#2b1410] hover:bg-[#faf3ee]"} ${!isVisible ? "opacity-60" : ""} p-2 sm:p-3 flex items-center gap-2`}>
            <div className="flex flex-col gap-0.5 shrink-0">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className={`p-0.5 rounded ${selectedMenuId === m.id ? "hover:bg-white/10 disabled:opacity-30" : "hover:bg-[#faf3ee] disabled:opacity-30"}`}>
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => move(idx, +1)} disabled={idx === menus.length - 1} className={`p-0.5 rounded ${selectedMenuId === m.id ? "hover:bg-white/10 disabled:opacity-30" : "hover:bg-[#faf3ee] disabled:opacity-30"}`}>
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
            </div>
            {m.image ? (
              <img src={m.image} alt="" className="h-10 w-10 rounded-md object-cover shrink-0" />
            ) : (
              <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${selectedMenuId === m.id ? "bg-white/10" : "bg-[#faf3ee]"}`}>
                <Utensils className="h-4 w-4" />
              </div>
            )}
            <button onClick={() => onSelect(m.id)} className="flex-1 text-left min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-medium truncate">{tr(m.name, "it")}</span>
                {!isVisible && <EyeOff className="h-3 w-3 shrink-0 opacity-70" />}
              </div>
              <div className={`text-xs truncate ${selectedMenuId === m.id ? "text-[#f7ebdf]/70" : "text-[#7c5a4e]"}`}>
                {m.availability && m.availability !== "always" ? `Solo ${m.availability}` : (tr(m.subtitle, "it") || "Sempre disponibile")}
              </div>
            </button>
            <ToggleSwitch checked={isVisible} onChange={toggleVisible} title={isVisible ? "Nascondi menu ai clienti" : "Mostra menu ai clienti"} />
            <button onClick={() => setEditingId(editingId === m.id ? null : m.id)} className={`p-1.5 rounded-md ${selectedMenuId === m.id ? "hover:bg-white/10" : "hover:bg-[#faf3ee]"}`}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => removeMenu(m)} className={`p-1.5 rounded-md ${selectedMenuId === m.id ? "hover:bg-white/10" : "hover:bg-red-50 text-red-700"}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        );})}
      </div>
      {editingId && (
        <MenuEditForm
          menu={menus.find((x) => x.id === editingId)}
          onDone={() => { setEditingId(null); onChange(); }}
          onCancel={() => setEditingId(null)}
        />
      )}
    </div>
  );
}

function MenuEditForm({ menu, onDone, onCancel }) {
  const [name, setName] = useState(menu.name);
  const [subtitle, setSubtitle] = useState(menu.subtitle || {it:""});
  const [icon, setIcon] = useState(menu.icon || "utensils");
  const [image, setImage] = useState(menu.image || "");
  const [availability, setAvailability] = useState(menu.availability || "always");
  const [waOrder, setWaOrder] = useState(!!menu.wa_order_enabled);
  const [waPhone, setWaPhone] = useState(menu.wa_order_phone || "");
  const [waPlace, setWaPlace] = useState(menu.wa_order_place_label || "");
  const [timeStart, setTimeStart] = useState(menu.time_start || "12:00");
  const [timeEnd, setTimeEnd] = useState(menu.time_end || "15:00");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const ICONS_LIST = ["utensils","wine","champagne","beer","glass-water","waves","cookie","coffee","sparkles","sofa","home","refrigerator","spa","flower"];
  const AVAIL_LIST = [
    { key: "always", label: "Sempre" },
    { key: "breakfast", label: "Colazione (07:00-10:30)" },
    { key: "lunch", label: "Pranzo (12:00-15:00)" },
    { key: "dinner", label: "Cena (19:00-22:30)" },
    { key: "custom", label: "Orario personalizzato" },
  ];

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name, subtitle, icon, image, availability, wa_order_enabled: waOrder, wa_order_phone: waPhone.replace(/\D/g, ""), wa_order_place_label: waPlace };
      if (availability === "custom") { payload.time_start = timeStart; payload.time_end = timeEnd; }
      else { payload.time_start = null; payload.time_end = null; }
      await updateMenu(menu.id, payload);
      toast({ title: "Menu aggiornato" });
      onDone();
    } catch (e) { toast({ title:"Errore", description: e.message }); }
    finally { setSaving(false); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setImage(url);
      toast({ title: "Immagine caricata" });
    } catch (err) {
      toast({ title: "Errore upload", description: err?.response?.data?.detail || err.message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="mt-3 p-4 rounded-lg bg-[#faf3ee] border border-[#e8dcc8] space-y-3">
      <MultiLangInput label="Nome menu" value={name} onChange={setName} />
      <MultiLangInput label="Sottotitolo" value={subtitle} onChange={setSubtitle} />

      {/* Cover image */}
      <div>
        <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">Foto di copertina</label>
        <div className="mt-1 flex items-start gap-3">
          {image ? (
            <div className="relative shrink-0">
              <img src={image} alt="cover" className="h-24 w-32 rounded-lg object-cover border border-[#e8dcc8]" />
              <button type="button" onClick={() => setImage("")} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white shadow border border-[#e8dcc8] text-red-600 flex items-center justify-center" title="Rimuovi">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="h-24 w-32 rounded-lg bg-white border-2 border-dashed border-[#e8dcc8] flex items-center justify-center text-[#c8b6a3] shrink-0">
              <ImageIcon className="h-6 w-6" />
            </div>
          )}
          <div className="flex-1 min-w-0 space-y-2">
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 rounded-md bg-white border border-[#e8dcc8] hover:bg-[#faf3ee] px-3 py-1.5 text-sm text-[#2b1410] disabled:opacity-60">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Caricamento..." : "Carica foto"}
            </button>
            <div className="text-xs text-[#7c5a4e]">JPG, PNG, WebP. Verrà mostrata sulla home invece dell'icona.</div>
            <input value={image?.startsWith("data:") ? "" : image} onChange={(e) => setImage(e.target.value)} placeholder="oppure URL immagine..." className="w-full rounded-md border border-[#e8dcc8] px-2 py-1 text-xs bg-white" />
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">Icona (se non c'è la foto)</label>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {ICONS_LIST.map((i) => (
            <button key={i} type="button" onClick={() => setIcon(i)} className={`text-xs px-2 py-1 rounded-full border ${icon === i ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "bg-white text-[#2b1410] border-[#e8dcc8]"}`}>{i}</button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Disponibilità</label>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {AVAIL_LIST.map((a) => (
            <button key={a.key} type="button" onClick={() => setAvailability(a.key)} className={`text-xs px-2 py-1 rounded-full border ${availability === a.key ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "bg-white text-[#2b1410] border-[#e8dcc8]"}`}>{a.label}</button>
          ))}
        </div>
        {availability === "custom" && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="text-[#7c5a4e]">Dalle</span>
            <input type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} className="rounded-md border border-[#e8dcc8] px-2 py-1 bg-white" />
            <span className="text-[#7c5a4e]">alle</span>
            <input type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} className="rounded-md border border-[#e8dcc8] px-2 py-1 bg-white" />
          </div>
        )}
      </div>

      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3" data-testid="menu-wa-order-block">
        <label className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
          <input type="checkbox" checked={waOrder} onChange={(e) => setWaOrder(e.target.checked)} data-testid="menu-wa-order-toggle" />
          Ordine via WhatsApp (piscina / spa / camera)
        </label>
        <p className="text-[11px] text-emerald-800/80 mt-1">Al posto di "Appunti" il cliente vede "Invia ordine": WhatsApp si apre con l'ordine già scritto in italiano. Nei menu senza questa opzione la lista resta solo un promemoria personale.</p>
        {waOrder && (
          <div className="grid gap-2 sm:grid-cols-2 mt-2">
            <label className="text-xs text-emerald-900">Numero WhatsApp che riceve (con prefisso)
              <input value={waPhone} onChange={(e) => setWaPhone(e.target.value)} placeholder="39351xxxxxxx" className="mt-1 w-full rounded-md border border-emerald-200 bg-white px-2 py-1.5 text-sm" data-testid="menu-wa-order-phone" />
            </label>
            <label className="text-xs text-emerald-900">Etichetta "dove sei" (vuoto = automatico)
              <input value={waPlace} onChange={(e) => setWaPlace(e.target.value)} placeholder="es. N° lettino · Postazione" className="mt-1 w-full rounded-md border border-emerald-200 bg-white px-2 py-1.5 text-sm" data-testid="menu-wa-order-place" />
            </label>
          </div>
        )}
        {waOrder && (
          <a href={`/qr-posti?menu=${menu.id}&n=30&label=${encodeURIComponent(waPlace.replace(/^N°\s*/i, "") || "Lettino")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-emerald-800 underline" data-testid="menu-wa-order-qr-link">
            Genera QR postazioni (30 lettini, stampabili) →
          </a>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded-md hover:bg-white">Annulla</button>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 rounded-md bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-sm disabled:opacity-60">
          <Save className="h-4 w-4" /> {saving ? "Salvo..." : "Salva"}
        </button>
      </div>
    </div>
  );
}

function CategoryBlock({ category, expanded, onToggle, onChange, onMoveUp, onMoveDown }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);
  const [subtitle, setSubtitle] = useState(category.subtitle || { it: "" });
  const [creatingDish, setCreatingDish] = useState(false);

  const save = async () => {
    await updateCategory(category.id, { name, subtitle });
    toast({ title: "Categoria salvata" });
    setEditing(false);
    await onChange();
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare la categoria "${tr(category.name, "it")}" e tutti i suoi piatti?`)) return;
    await deleteCategory(category.id);
    toast({ title: "Categoria eliminata" });
    await onChange();
  };

  const addDish = async () => {
    setCreatingDish(true);
    try {
      const nextOrder = (category.items?.[category.items.length - 1]?.order || 0) + 1;
      await createDish({
        categoryId: category.id,
        name: { it: "Nuovo piatto", en: "New dish", es: "Nuevo", fr: "Nouveau", de: "Neu" },
        description: { it: "", en: "", es: "", fr: "", de: "" },
        prices: [{ label: null, value: 0 }],
        allergens: [], mayContain: [], labels: [], image: "",
        order: nextOrder,
      });
      toast({ title: "Piatto creato" });
      await onChange();
    } finally { setCreatingDish(false); }
  };

  const isVisible = category.visible !== false;
  const toggleVisible = async () => {
    try {
      await updateCategory(category.id, { visible: !isVisible });
      toast({ title: isVisible ? "Categoria nascosta" : "Categoria visibile" });
      await onChange();
    } catch (e) { toast({ title: "Errore", description: e.message }); }
  };

  const onMoveDish = async (from, to) => {
    const items = [...(category.items || [])];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    // Optimistically update local order to keep UI snappy
    const payload = items.map((it, i) => ({ id: it.id, order: i }));
    try {
      const { reorderDishes } = await import("../lib/api");
      await reorderDishes(payload);
      await onChange();
    } catch (e) {
      toast({ title: "Errore riordino", description: e.message });
    }
  };

  return (
    <div className={`bg-white rounded-xl border border-[#e8dcc8] overflow-hidden ${!isVisible ? "opacity-70" : ""}`}>
      <div className="flex items-center justify-between px-4 py-3 bg-[#faf3ee] border-b border-[#e8dcc8]">
        <button onClick={onToggle} className="flex items-center gap-2 flex-1 text-left min-w-0">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <span className="font-serif text-lg font-semibold text-[#2b1410] truncate">{tr(category.name, "it")}</span>
          {!isVisible && <EyeOff className="h-3.5 w-3.5 text-neutral-400 shrink-0" title="Nascosta ai clienti" />}
          <span className="text-xs text-[#7c5a4e] shrink-0">{(category.items || []).length} piatti</span>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex flex-col -my-1">
            <button onClick={onMoveUp} disabled={!onMoveUp} className="p-0.5 rounded hover:bg-white text-[#4a1c17] disabled:opacity-25" title="Sposta su" data-testid={`category-move-up-${category.id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
            <button onClick={onMoveDown} disabled={!onMoveDown} className="p-0.5 rounded hover:bg-white text-[#4a1c17] disabled:opacity-25" title="Sposta giù" data-testid={`category-move-down-${category.id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
          </div>
          <ToggleSwitch checked={isVisible} onChange={toggleVisible} title={isVisible ? "Nascondi categoria" : "Mostra categoria"} />
          <button onClick={() => setEditing((v) => !v)} className="p-2 rounded-md hover:bg-white text-[#4a1c17]"><Pencil className="h-4 w-4" /></button>
          <button onClick={remove} className="p-2 rounded-md hover:bg-red-50 text-red-700"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      {editing && (
        <div className="p-4 space-y-3 bg-[#faf3ee]/50 border-b border-[#e8dcc8]">
          <MultiLangInput label="Nome categoria" value={name} onChange={setName} />
          <MultiLangInput label="Sottotitolo" value={subtitle} onChange={setSubtitle} />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm rounded-md hover:bg-white">Annulla</button>
            <button onClick={save} className="inline-flex items-center gap-1 rounded-md bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-sm">
              <Save className="h-4 w-4" /> Salva
            </button>
          </div>
        </div>
      )}

      {expanded && (
        <div className="p-4 space-y-3">
          {(category.items || []).map((dish, idx) => (
            <DishRow
              key={dish.id}
              dish={dish}
              onChange={onChange}
              onMoveUp={idx > 0 ? () => onMoveDish(idx, idx - 1) : null}
              onMoveDown={idx < (category.items || []).length - 1 ? () => onMoveDish(idx, idx + 1) : null}
            />
          ))}
          <button onClick={addDish} disabled={creatingDish} className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-[#e8dcc8] text-[#7c5a4e] hover:text-[#4a1c17] hover:border-[#4a1c17] py-2.5 text-sm">
            <Plus className="h-4 w-4" /> Aggiungi piatto
          </button>
        </div>
      )}
    </div>
  );
}

function DishRow({ dish, onChange, onMoveUp, onMoveDown }) {
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHint, setAiHint] = useState(null);
  const fileRef = useRef(null);
  const [d, setD] = useState({
    ...dish,
    name: dish.name || { it: "" },
    description: dish.description || { it: "" },
    prices: dish.prices?.length ? dish.prices : [{ label: null, value: 0 }],
    allergens: dish.allergens || [],
    mayContain: dish.mayContain || [],
    intolerances: dish.intolerances || [],
    labels: dish.labels || [],
    image: dish.image || "",
    link: dish.link || "",
    featured: dish.featured || false,
  });
  const [cassa, setCassa] = useState({ enabled: undefined, category_id: "", department_id: "", description: "", label: "", receipt: "" });

  const save = async () => {
    await updateDish(dish.id, {
      name: d.name,
      description: d.description,
      prices: d.prices.map((p) => ({ label: p.label || null, value: parseFloat(p.value) || 0 })),
      allergens: d.allergens,
      mayContain: d.mayContain,
      intolerances: d.intolerances,
      labels: d.labels,
      image: d.image,
      link: d.link.trim() || null,
      featured: d.featured,
    });
    if (cassa.enabled) {
      if (!cassa.category_id || !cassa.department_id) {
        toast({ title: "Piatto salvato, ma non inviato in Cassa", description: "Scegli categoria e reparto Cassa in Cloud" });
      } else {
        try {
          const res = await pushDishToCassa(dish.id, { ...cassa, description: (cassa.description || d.name?.it || "").trim() });
          toast({ title: dish.cassa ? "Piatto salvato e aggiornato in Cassa" : "Piatto salvato e inviato in Cassa", description: `${res.products.length} prodott${res.products.length === 1 ? "o" : "i"} su Cassa in Cloud` });
        } catch (e) {
          toast({ title: "Piatto salvato, errore Cassa in Cloud", description: e.message });
        }
      }
    } else {
      toast({ title: "Piatto salvato" });
    }
    setEditing(false);
    await onChange();
  };

  const suggestAI = async () => {
    setAiLoading(true);
    setAiHint(null);
    try {
      const tok = localStorage.getItem("admin_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dishes/${dish.id}/suggest-allergens`, {
        method: "POST", headers: { Authorization: `Bearer ${tok}` },
      });
      if (!r.ok) throw new Error((await r.json())?.detail || "Errore AI");
      const data = await r.json();
      setD((prev) => ({
        ...prev,
        allergens: data.suggested_allergens || [],
        mayContain: data.suggested_may_contain || [],
        intolerances: data.suggested_intolerances || [],
      }));
      setAiHint(data.reasoning);
      toast({ title: "AI ha suggerito 3 gruppi", description: "Contiene · Tracce · Intolleranze — rivedi e Salva" });
    } catch (e) {
      toast({ title: "Errore AI", description: e.message });
    } finally {
      setAiLoading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare "${tr(dish.name, "it")}"?`)) return;
    await deleteDish(dish.id);
    toast({ title: "Piatto eliminato" });
    await onChange();
  };

  const toggleFeatured = async () => {
    try {
      await updateDish(dish.id, { featured: !dish.featured });
      toast({ title: !dish.featured ? "Aggiunto a Menu del Giorno" : "Rimosso da Menu del Giorno" });
      await onChange();
    } catch (e) {
      toast({ title: "Errore", description: e.message });
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setD((prev) => ({ ...prev, image: url }));
      toast({ title: "Immagine caricata" });
    } catch (err) {
      toast({ title: "Errore upload", description: err?.response?.data?.detail || err.message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleVisible = async () => {
    const cur = d.visible !== false;
    // Optimistic UI: aggiorna subito lo stato locale così l'interruttore
    // reagisce istantaneamente al click. Rollback in caso di errore API.
    setD({ ...d, visible: !cur });
    try {
      await updateDish(dish.id, { visible: !cur });
      toast({ title: cur ? "Piatto nascosto" : "Piatto visibile" });
      await onChange();
    } catch (e) {
      setD({ ...d, visible: cur }); // rollback
      toast({ title: "Errore", description: e.message });
    }
  };

  const isVisible = d.visible !== false;

  return (
    <div className={`rounded-lg border overflow-hidden ${!isVisible ? "opacity-60" : ""} ${dish.featured ? "border-amber-300 ring-2 ring-amber-100" : "border-[#e8dcc8]"}`}>
      <div className="p-3 bg-white">
        <div className="flex items-center gap-3">
          <div className="flex flex-col shrink-0">
            <button
              type="button"
              onClick={onMoveUp || undefined}
              disabled={!onMoveUp}
              title="Sposta su"
              data-testid={`dish-move-up-${dish.id}`}
              className="p-0.5 text-[#7c5a4e] hover:text-[#4a1c17] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onMoveDown || undefined}
              disabled={!onMoveDown}
              title="Sposta giù"
              data-testid={`dish-move-down-${dish.id}`}
              className="p-0.5 text-[#7c5a4e] hover:text-[#4a1c17] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
          {/* Mobile: clickable area to expand edit. Desktop: same, but with dedicated Pencil below */}
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="flex items-center gap-3 flex-1 min-w-0 text-left"
            data-testid={`dish-row-expand-${dish.id}`}
          >
            {dish.image ? (
              <img src={dish.image} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />
            ) : (
              <div className="h-14 w-14 rounded-lg bg-[#faf3ee] shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium text-[#2b1410] text-sm md:text-base break-words">{tr(dish.name, "it")}</div>
                {!isVisible && <EyeOff className="h-3.5 w-3.5 text-neutral-400 shrink-0" title="Nascosto ai clienti" />}
                {dish.featured && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
                    <Sparkles className="h-3 w-3" /> Del giorno
                  </span>
                )}
              </div>
              <div className="text-xs text-[#7c5a4e] mt-0.5">
                {dish.prices?.map((p, i) => (
                  <span key={i}>{i > 0 && " · "}{p.label ? `${p.label}: ` : ""}{Number(p.value).toFixed(2)} €</span>
                ))}
              </div>
            </div>
          </button>
          {/* Toggle: always visible (mobile + desktop) */}
          <ToggleSwitch size="lg" checked={isVisible} onChange={toggleVisible} title={isVisible ? "Nascondi piatto ai clienti" : "Mostra piatto ai clienti"} />
          {/* Desktop-only extra icons */}
          <div className="hidden md:flex items-center gap-1">
            <div className="w-2" aria-hidden />
            <button onClick={toggleFeatured} title={dish.featured ? "Rimuovi dal Menu del giorno" : "Aggiungi al Menu del giorno"} className={`p-2 rounded-md ${dish.featured ? "bg-amber-50 text-amber-700 hover:bg-amber-100" : "hover:bg-[#faf3ee] text-[#7c5a4e]"}`}>
              <Sparkles className="h-4 w-4" />
            </button>
            <button onClick={() => setEditing((v) => !v)} className="p-2 rounded-md hover:bg-[#faf3ee] text-[#4a1c17]" data-testid={`dish-edit-${dish.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={remove} className="p-2 rounded-md hover:bg-red-50 text-red-700"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {editing && (
        <div className="border-t border-[#e8dcc8] p-4 bg-[#faf3ee]/40 space-y-3">
          <MultiLangInput label="Nome" value={d.name} onChange={(v) => setD({ ...d, name: v })} />
          <MultiLangInput label="Descrizione" value={d.description} onChange={(v) => setD({ ...d, description: v })} type="textarea" />

          <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">Prezzi</label>
            <div className="mt-1 space-y-2">
              {d.prices.map((p, i) => (
                <div key={`price-${i}-${p.label ?? "std"}`} className="flex items-center gap-2">
                  <input
                    placeholder="Etichetta (opzionale)"
                    value={p.label || ""}
                    onChange={(e) => {
                      const c = [...d.prices]; c[i] = { ...c[i], label: e.target.value || null }; setD({ ...d, prices: c });
                    }}
                    className="flex-1 rounded-md border border-[#e8dcc8] px-2 py-1 text-sm bg-white"
                  />
                  <input
                    type="number" step="0.01" placeholder="0.00"
                    value={p.value}
                    onChange={(e) => {
                      const c = [...d.prices]; c[i] = { ...c[i], value: e.target.value }; setD({ ...d, prices: c });
                    }}
                    className="w-24 rounded-md border border-[#e8dcc8] px-2 py-1 text-sm bg-white"
                  />
                  <span className="text-sm text-[#7c5a4e]">€</span>
                  <button onClick={() => setD({ ...d, prices: d.prices.filter((_, k) => k !== i) })} className="p-1.5 rounded-md hover:bg-red-50 text-red-700 disabled:opacity-40" disabled={d.prices.length === 1}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button onClick={() => setD({ ...d, prices: [...d.prices, { label: "", value: 0 }] })} className="text-sm text-[#4a1c17] hover:underline">+ Aggiungi variante</button>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">Immagine</label>
            <div className="mt-1 flex items-start gap-3">
              {d.image ? (
                <div className="relative shrink-0">
                  <img src={d.image} alt="preview" className="h-20 w-20 rounded-lg object-cover border border-[#e8dcc8]" />
                  <button
                    type="button"
                    onClick={() => setD({ ...d, image: "" })}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white shadow border border-[#e8dcc8] text-red-600 flex items-center justify-center"
                    title="Rimuovi immagine"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div className="h-20 w-20 rounded-lg bg-white border-2 border-dashed border-[#e8dcc8] flex items-center justify-center text-[#c8b6a3] shrink-0">
                  <Upload className="h-5 w-5" />
                </div>
              )}
              <div className="flex-1 min-w-0 space-y-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 rounded-md bg-white border border-[#e8dcc8] hover:bg-[#faf3ee] px-3 py-1.5 text-sm text-[#2b1410] disabled:opacity-60"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "Caricamento..." : "Carica foto"}
                </button>
                <div className="text-xs text-[#7c5a4e]">JPG, PNG, WebP o GIF · max 3MB</div>
                <input
                  value={d.image?.startsWith("data:") ? "" : d.image}
                  onChange={(e) => setD({ ...d, image: e.target.value })}
                  placeholder="oppure incolla un URL..."
                  className="w-full rounded-md border border-[#e8dcc8] px-2 py-1 text-xs bg-white"
                />
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={d.featured}
              onChange={(e) => setD({ ...d, featured: e.target.checked })}
              className="h-4 w-4 rounded border-[#e8dcc8] accent-[#4a1c17]"
            />
            <span className="text-sm text-[#2b1410]">Aggiungi al Menu del Giorno</span>
            <Sparkles className="h-3.5 w-3.5 text-amber-600" />
          </label>

          <CodePicker title="Etichette" options={LABELS} selected={d.labels} onChange={(v) => setD({ ...d, labels: v })} />
          <CassaDishBox dish={{ ...dish, prices: d.prices }} nameIt={d.name?.it || ""} value={cassa} onChange={setCassa} />
          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">AI Sicurezza alimentare</div>
              <button
                type="button"
                onClick={suggestAI}
                disabled={aiLoading}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 hover:text-amber-900 disabled:opacity-50"
                data-testid={`dish-ai-allergens-${dish.id}`}
              >
                {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {aiLoading ? "Analisi…" : "Suggerisci con AI · Contiene · Tracce · Intolleranze"}
              </button>
            </div>
            <div className="text-[11px] text-[#7c5a4e] mb-2">L'AI analizza il piatto e compila i 3 gruppi qui sotto. Rivedi e salva.</div>
            {aiHint && (
              <div className="mt-1 text-[11px] text-amber-800 italic">💡 {aiHint}</div>
            )}
          </div>
          <CodePicker title="Contiene (Allergeni)" options={ALLERGENS} selected={d.allergens} onChange={(v) => setD({ ...d, allergens: v })} />
          <CodePicker title="Può contenere tracce" options={ALLERGENS} selected={d.mayContain} onChange={(v) => setD({ ...d, mayContain: v })} />
          <CodePicker title="Intolleranze" options={INTOLERANCES} selected={d.intolerances} onChange={(v) => setD({ ...d, intolerances: v })} />

          <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">Link CTA (opzionale)</label>
            <input
              type="text"
              value={d.link}
              onChange={(e) => setD({ ...d, link: e.target.value })}
              placeholder="/room/argo · /spa · https://…"
              data-testid="dish-link-input"
              className="mt-1 w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white font-mono"
            />
            <p className="mt-1 text-[11px] text-[#7c5a4e]">Se compilato, sostituisce il tasto "+" con un tasto "Apri" che porta al link (es. <code>/room/argo</code> per la domotica della stanza).</p>
          </div>

          <div className="flex justify-between gap-2 flex-wrap items-center">
            <div className="flex items-center gap-2 md:hidden">
              <button
                onClick={toggleFeatured}
                type="button"
                title={dish.featured ? "Rimuovi dal Menu del giorno" : "Aggiungi al Menu del giorno"}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm ${dish.featured ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-white border border-[#e8dcc8] text-[#7c5a4e]"}`}
              >
                <Sparkles className="h-4 w-4" /> Del giorno
              </button>
              <button
                onClick={remove}
                type="button"
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm bg-white border border-red-200 text-red-700"
              >
                <Trash2 className="h-4 w-4" /> Elimina
              </button>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm rounded-md hover:bg-white">Annulla</button>
              <button onClick={save} className="inline-flex items-center gap-1 rounded-md bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-sm">
                <Save className="h-4 w-4" /> Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function CodePicker({ title, options, selected, onChange }) {
  const toggle = (code) => {
    if (selected.includes(code)) onChange(selected.filter((c) => c !== code));
    else onChange([...selected, code]);
  };
  return (
    <div>
      <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">{title}</label>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {Object.entries(options).map(([code, meta]) => {
          const on = selected.includes(code);
          return (
            <button
              key={code}
              type="button"
              onClick={() => toggle(code)}
              className={`text-xs px-2 py-1 rounded-full border ${on ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "bg-white text-[#2b1410] border-[#e8dcc8] hover:bg-[#faf3ee]"}`}
            >
              {meta.it}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Restaurant editor ----------------
function RestaurantEditor({ restaurant, onChange }) {
  const [r, setR] = useState(restaurant);
  const [saving, setSaving] = useState(false);

  useEffect(() => setR(restaurant), [restaurant]);

  const save = async () => {
    setSaving(true);
    try {
      await updateRestaurant({
        name: r.name,
        tagline: r.tagline,
        city: r.city,
        rating: parseFloat(r.rating) || 0,
        reviews: parseInt(r.reviews) || 0,
        logo: r.logo,
        info: r.info,
      });
      toast({ title: "Ristorante aggiornato" });
      await onChange();
    } catch (e) {
      toast({ title: "Errore", description: e.message });
    } finally { setSaving(false); }
  };

  const setField = (key, val) => setR({ ...r, [key]: val });
  const setInfo = (key, val) => setR({ ...r, info: { ...r.info, [key]: val } });

  return (
    <div className="max-w-2xl">
      <h2 className="font-serif text-2xl font-bold text-[#2b1410]">Informazioni Ristorante</h2>

      <div className="mt-4 space-y-4 bg-white border border-[#e8dcc8] rounded-xl p-5">
        <Field label="Nome" value={r.name} onChange={(v) => setField("name", v)} />
        <Field label="Tagline" value={r.tagline} onChange={(v) => setField("tagline", v)} />
        <Field label="Città" value={r.city} onChange={(v) => setField("city", v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Rating" type="number" step="0.1" value={r.rating} onChange={(v) => setField("rating", v)} />
          <Field label="Numero recensioni" type="number" value={r.reviews} onChange={(v) => setField("reviews", v)} />
        </div>
        <Field label="URL Logo" value={r.logo} onChange={(v) => setField("logo", v)} />
      </div>

      <h3 className="mt-6 font-serif text-xl font-bold text-[#2b1410]">Contatti</h3>
      <div className="mt-3 space-y-4 bg-white border border-[#e8dcc8] rounded-xl p-5">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Wi-Fi nome" value={r.info.wifi.name} onChange={(v) => setInfo("wifi", { ...r.info.wifi, name: v })} />
          <Field label="Wi-Fi password" value={r.info.wifi.password} onChange={(v) => setInfo("wifi", { ...r.info.wifi, password: v })} />
        </div>
        <Field label="Telefono" value={r.info.phone} onChange={(v) => setInfo("phone", v)} />
        <Field label="Email" value={r.info.email} onChange={(v) => setInfo("email", v)} />
        <Field label="Sito web" value={r.info.website} onChange={(v) => setInfo("website", v)} />
        <Field label="Indirizzo" value={r.info.address} onChange={(v) => setInfo("address", v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Instagram URL" value={r.info.social.instagram} onChange={(v) => setInfo("social", { ...r.info.social, instagram: v })} />
          <Field label="Facebook URL" value={r.info.social.facebook} onChange={(v) => setInfo("social", { ...r.info.social, facebook: v })} />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-5 py-2 text-sm font-medium disabled:opacity-60">
          <Save className="h-4 w-4" /> {saving ? "Salvataggio..." : "Salva tutto"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e]">{label}</label>
      <input
        type={type}
        step={step}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-[#e8dcc8] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/20"
      />
    </div>
  );
}

// ---------------- Spa bookings editor ----------------
function SpaBookingsEditor() {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState({ bookings: [], slots: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newBooking, setNewBooking] = useState(null); // {date, slot_id}

  const days = useMemo(() => {
    return [0,1,2,3,4,5,6].map((i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const from = isoLocal(days[0]);
      const to = isoLocal(days[6]);
      const res = await fetchAdminSpaBookings(from, to);
      setData(res);
    } catch (e) {
      toast({ title: "Errore", description: e?.message });
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);
  const { blockFor, reload: reloadBlocks } = useSlotBlocks("spa", isoLocal(days[0]), isoLocal(days[6]));

  const byDaySlot = useMemo(() => {
    const map = {};
    for (const b of data.bookings) {
      if (b.status === "cancelled") continue;
      map[`${b.date}::${b.slot_id}`] = b;
    }
    return map;
  }, [data.bookings]);

  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate()+7); setWeekStart(d); };
  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate()-7); setWeekStart(d); };
  const thisWeek = () => setWeekStart(mondayOf(new Date()));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="font-serif text-2xl font-bold text-[#2b1410]">Prenotazioni Spa</h2>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={prevWeek} className="rounded-md border border-[#e8dcc8] px-3 py-1.5 text-sm hover:bg-white" data-testid="spa-prev-week">← Settimana</button>
          <button onClick={thisWeek} className="rounded-md border border-[#e8dcc8] px-3 py-1.5 text-sm hover:bg-white" data-testid="spa-this-week">Oggi</button>
          <button onClick={nextWeek} className="rounded-md border border-[#e8dcc8] px-3 py-1.5 text-sm hover:bg-white" data-testid="spa-next-week">Settimana →</button>
          <ClosePeriodButton service="spa" onDone={reloadBlocks} testPrefix="spa" />
        </div>
      </div>
      <p className="text-sm text-[#7c5a4e] mb-3">
        Settimana dal <strong>{fmtItShort(days[0])}</strong> al <strong>{fmtItShort(days[6])}</strong>.
        <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-200 border border-emerald-400" /> libero</span>
        <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-200 border border-red-400" /> da confermare</span>
        <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-purple-200 border border-purple-400" /> confermata via WhatsApp</span>
        <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-neutral-200 border border-neutral-400" /> chiuso</span>
      </p>
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
      ) : (
        <div className="overflow-x-auto border border-[#e8dcc8] rounded-xl bg-white">
          <table className="w-full text-xs sm:text-sm" data-testid="spa-week-grid">
            <thead>
              <tr className="bg-[#faf3ee]">
                <th className="p-2 text-left text-[#7c5a4e] font-semibold uppercase tracking-wide">Turno</th>
                {days.map((d, i) => (
                  <th key={i} className={`p-2 text-center ${d.toDateString() === new Date().toDateString() ? "bg-[#4a1c17]/10" : ""}`}>
                    <div className="text-[10px] text-[#7c5a4e] uppercase">{DOW_SHORT[d.getDay()]}</div>
                    <div className="font-serif text-[#2b1410]">{d.getDate()}/{d.getMonth()+1}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.slots || []).map((s) => (
                <tr key={s.id} className="border-t border-[#e8dcc8]">
                  <td className="p-2 font-semibold text-[#2b1410] whitespace-nowrap">{s.label}</td>
                  {days.map((d, di) => {
                    const iso = isoLocal(d);
                    const b = byDaySlot[`${iso}::${s.id}`];
                    return (
                      <td key={di} className="p-1">
                        {b ? (
                          <button
                            onClick={() => setSelected(b)}
                            className={`w-full rounded-md border px-2 py-1.5 text-left transition-colors ${
                              b.whatsapp_notified
                                ? "bg-purple-100 border-purple-300 hover:bg-purple-200 text-purple-900"
                                : "bg-red-100 border-red-300 hover:bg-red-200 text-red-900"
                            }`}
                            data-testid={`spa-cell-booked-${iso}-${s.id}`}
                            title={b.whatsapp_notified ? "Confermata via WhatsApp" : "Da confermare via WhatsApp"}
                          >
                            <div className="font-semibold text-[11px] truncate">{b.name}</div>
                            <div className="text-[10px] opacity-80 truncate">{b.phone} · {b.num_people}p</div>
                          </button>
                        ) : blockFor(iso, s.id) ? (
                          <BlockedSlotCell block={blockFor(iso, s.id)} service="spa" iso={iso} slotId={s.id} testPrefix="spa" onChanged={reloadBlocks} />
                        ) : (
                          <FreeSlotCell service="spa" iso={iso} slotId={s.id} testPrefix="spa" onNewBooking={() => setNewBooking({ date: iso, slot_id: s.id })} onChanged={reloadBlocks} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <BookingDetailModal booking={selected} slots={data.slots} bookings={data.bookings} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); load(); }} />
      )}
      {newBooking && (
        <NewBookingModal init={newBooking} slots={data.slots} onClose={() => setNewBooking(null)} onCreated={() => { setNewBooking(null); load(); }} />
      )}
    </div>
  );
}

const DOW_SHORT = ["dom","lun","mar","mer","gio","ven","sab"];
function isoLocal(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }
function mondayOf(d) { const dt = new Date(d); const day = (dt.getDay() + 6) % 7; dt.setDate(dt.getDate() - day); dt.setHours(0,0,0,0); return dt; }
function fmtItShort(d) { return `${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`; }
const _MONTHS_IT_ADM = ["gennaio","febbraio","marzo","aprile","maggio","giugno","luglio","agosto","settembre","ottobre","novembre","dicembre"];
const _DAYS_IT_ADM = ["Domenica","Lunedì","Martedì","Mercoledì","Giovedì","Venerdì","Sabato"];
function formatBookingDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${_DAYS_IT_ADM[dt.getDay()]} ${d} ${_MONTHS_IT_ADM[m - 1]} ${y}`;
}

function BookingDetailModal({ booking, slots, bookings, onClose, onChanged }) {
  const [mode, setMode] = useState("view"); // view | edit
  const [form, setForm] = useState({
    date: booking.date,
    slot_id: booking.slot_id,
    name: booking.name || "",
    email: booking.email || "",
    phone: booking.phone || "",
    num_people: booking.num_people || 2,
    notes: booking.notes || "",
  });
  const [saving, setSaving] = useState(false);

  // slots taken on the selected date (excluding this booking) — for the move dropdown
  const takenOnDate = useMemo(() => {
    const s = new Set();
    (bookings || []).forEach((b) => {
      if (b.id !== booking.id && b.status === "confirmed" && b.date === form.date) s.add(b.slot_id);
    });
    return s;
  }, [bookings, form.date, booking.id]);

  const cancel = async () => {
    if (!window.confirm("Confermare l'annullamento della prenotazione?")) return;
    try { await cancelSpaBooking(booking.id); toast({ title: "Prenotazione annullata" }); onChanged(); }
    catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e?.message }); }
  };
  const del = async () => {
    if (!window.confirm("Eliminare definitivamente questa prenotazione dallo storico?")) return;
    try { await deleteSpaBooking(booking.id); toast({ title: "Eliminata" }); onChanged(); }
    catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e?.message }); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await updateSpaBooking(booking.id, {
        date: form.date,
        slot_id: Number(form.slot_id),
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        num_people: Number(form.num_people),
        notes: form.notes.trim(),
      });
      toast({ title: "Prenotazione aggiornata" });
      onChanged();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  const markWaOpened = async (e) => {
    // Mark as notified so the grid cell turns purple, then let the browser
    // follow the wa.me link naturally (no preventDefault → real navigation).
    if (!booking.whatsapp_notified) {
      try {
        await updateSpaBooking(booking.id, { whatsapp_notified: true });
        onChanged();
      } catch (err) {
        // non-blocking: even if we fail to persist the flag, still open WhatsApp
        if (process.env.NODE_ENV === "development") console.warn("Failed to mark whatsapp_notified", err);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="spa-booking-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-serif text-xl font-bold text-[#2b1410]">
            {mode === "edit" ? "Modifica prenotazione" : "Dettaglio prenotazione"}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="h-4 w-4" /></button>
        </div>

        {mode === "view" ? (
          <>
            <div className="mt-3 space-y-2 text-sm text-[#2b1410]">
              <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#4a1c17]" /> <strong>{formatBookingDate(booking.date)}</strong></div>
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#4a1c17]" /> Turno #{booking.slot_id + 1}</div>
              <div><span className="text-[#7c5a4e]">Cliente:</span> <strong>{booking.name}</strong></div>
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#4a1c17]" /> <a href={`tel:${booking.phone}`} className="text-[#4a1c17] hover:underline">{booking.phone}</a></div>
              <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#4a1c17]" /> <a href={`mailto:${booking.email}`} className="text-[#4a1c17] hover:underline break-all">{booking.email}</a></div>
              <div><span className="text-[#7c5a4e]">Persone:</span> {booking.num_people}</div>
              <div>
                <span className="text-[#7c5a4e]">Totale:</span> <strong>€ {booking.price}</strong>
                {booking.price_per_person ? (<span className="text-[#7c5a4e]"> (€ {booking.price_per_person} × {booking.num_people})</span>) : null}
              </div>
              {booking.notes && <div className="pt-1 text-[13px] text-[#7c5a4e] border-t border-neutral-100"><em>Note:</em> {booking.notes}</div>}
              {booking.status === "cancelled" && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-xs">ANNULLATA</div>
              )}
              {booking.whatsapp_notified && booking.status !== "cancelled" && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-purple-100 text-purple-800 px-2 py-0.5 text-xs">
                  <MessageCircle className="h-3 w-3" /> Confermata via WhatsApp
                </div>
              )}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {(booking.tuya_spa_pin || booking.ttlock_pin) && (
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("admin_token");
                      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/spa/bookings/${booking.id}/notify`, {
                        method: "POST",
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      const data = await r.json();
                      openWhatsApp(data.guest_wa_url);
                      // Backend now stamps whatsapp_notified=true; refresh the
                      // grid so the cell turns purple and the button too.
                      onChanged?.();
                    } catch (e) { /* silent */ }
                  }}
                  data-testid="spa-pin-wa-btn"
                  className={`inline-flex items-center gap-1.5 rounded-full text-white px-4 py-2 text-sm font-medium transition-colors ${
                    booking.whatsapp_notified
                      ? "bg-purple-600 hover:bg-purple-700"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                  title={booking.whatsapp_notified ? "Prenotazione confermata via WhatsApp — clicca per re-inviare" : `Codice ${booking.tuya_spa_pin || booking.ttlock_pin}`}
                >
                  <MessageCircle className="h-4 w-4" />
                  {booking.whatsapp_notified ? "Confermata via WhatsApp" : `Invia PIN Spa · ${booking.tuya_spa_pin || booking.ttlock_pin}`}
                </button>
              )}
              {booking.whatsapp_url && (
                <a
                  {...waAnchorProps(booking.whatsapp_url)}
                  onClick={markWaOpened}
                  data-testid="spa-whatsapp-btn"
                  title={booking.whatsapp_notified ? "Già confermata via WhatsApp — clicca per re-inviare" : "Invia messaggio WhatsApp di conferma al cliente"}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white ${
                    booking.whatsapp_notified ? "bg-purple-600 hover:bg-purple-700" : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  <MessageCircle className="h-4 w-4" />
                  {booking.whatsapp_notified ? "WhatsApp inviato" : "Invia WhatsApp"}
                </a>
              )}
              <button
                onClick={() => setMode("edit")}
                data-testid="spa-edit-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-2 text-sm font-medium"
              >
                <Pencil className="h-4 w-4" /> Modifica / Sposta
              </button>
              {booking.status !== "cancelled" && (
                <button
                  onClick={cancel}
                  data-testid="spa-cancel-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 text-sm font-medium"
                >
                  Annulla
                </button>
              )}
              <button
                onClick={del}
                data-testid="spa-delete-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-neutral-200 hover:bg-neutral-300 text-neutral-800 px-3 py-2 text-sm font-medium ml-auto"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </>
        ) : (
          <BookingFormFields form={form} setForm={setForm} slots={slots} takenSlotIds={takenOnDate} disableFieldsForCancelled={booking.status === "cancelled"}
            actions={
              <div className="mt-5 flex flex-wrap gap-2">
                <button onClick={() => setMode("view")} className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200 px-4 py-2 text-sm">Annulla modifiche</button>
                <button onClick={save} disabled={saving} data-testid="spa-save-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-2 text-sm font-medium ml-auto disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
                </button>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}

function NewBookingModal({ init, slots, onClose, onCreated }) {
  const [form, setForm] = useState({
    date: init.date,
    slot_id: init.slot_id,
    name: "", email: "", phone: "",
    num_people: 2, notes: "",
  });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast({ title: "Nome e telefono obbligatori" });
      return;
    }
    setSaving(true);
    try {
      await adminCreateSpaBooking({
        date: form.date,
        slot_id: Number(form.slot_id),
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        num_people: Number(form.num_people),
        notes: form.notes.trim(),
      });
      toast({ title: "Prenotazione creata" });
      onCreated();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="spa-new-booking-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-serif text-xl font-bold text-[#2b1410]">Nuova prenotazione</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <BookingFormFields
          form={form} setForm={setForm} slots={slots} takenSlotIds={new Set()}
          actions={
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200 px-4 py-2 text-sm">Annulla</button>
              <button onClick={create} disabled={saving} data-testid="spa-create-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-2 text-sm font-medium disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crea prenotazione
              </button>
            </div>
          }
        />
      </div>
    </div>
  );
}

// Slot price helper (mirror of backend logic — for showing € in the admin editor)
function slotPricePerPerson(slotId, iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const wd = (new Date(y, m - 1, d).getDay() + 6) % 7; // Mon=0..Sun=6
  if (slotId === 4) return wd >= 4 ? 150 : 100;
  return wd >= 5 ? 60 : 50;
}

function BookingFormFields({ form, setForm, slots, takenSlotIds, disableFieldsForCancelled, actions }) {
  const perPerson = slotPricePerPerson(Number(form.slot_id), form.date);
  const total = perPerson * Number(form.num_people || 0);
  return (
    <div className="mt-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Data</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="spa-form-date"
            className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Turno</label>
          <select value={form.slot_id} onChange={(e) => setForm({ ...form, slot_id: Number(e.target.value) })}
            data-testid="spa-form-slot"
            className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white">
            {(slots || []).map((s) => (
              <option key={s.id} value={s.id} disabled={takenSlotIds && takenSlotIds.has(s.id)}>
                {s.label}{takenSlotIds && takenSlotIds.has(s.id) ? " (occupato)" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Nome cliente</label>
        <CustomerAutocomplete value={form.name} onChange={(v) => setForm({ ...form, name: v })} search={searchAdminCustomers} placeholder="Nome (cerca in archivio: hotel, spa, ristorante…)"
          testId="spa-form-name" className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white"
          onPick={(c) => { setForm({ ...form, name: c.name, phone: c.phone || form.phone, email: c.email || form.email }); toast({ title: `Cliente trovato: ${c.name}` }); }} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Telefono</label>
          <CustomerAutocomplete value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} search={searchAdminCustomers} placeholder="+39…" inputMode="tel" minChars={3}
            testId="spa-form-phone" className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white"
            onPick={(c) => { setForm({ ...form, name: c.name, phone: c.phone || form.phone, email: c.email || form.email }); toast({ title: `Cliente trovato: ${c.name}` }); }} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Email (opz.)</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="spa-form-email"
            className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Persone</label>
          <select value={form.num_people} onChange={(e) => setForm({ ...form, num_people: Number(e.target.value) })}
            data-testid="spa-form-people"
            className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white">
            {[2,3,4].map((n) => <option key={n} value={n}>{n} persone</option>)}
          </select>
        </div>
        <div className="rounded-md bg-[#4a1c17] text-[#f7ebdf] px-3 py-2 flex flex-col justify-center">
          <div className="text-[10px] uppercase tracking-wider opacity-70">Totale</div>
          <div className="text-lg font-serif font-bold" data-testid="spa-form-total">€ {total}</div>
          <div className="text-[10px] opacity-70">€ {perPerson} × {form.num_people}</div>
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Note</label>
        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
          data-testid="spa-form-notes"
          className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white" />
      </div>
      {actions}
    </div>
  );
}

// ---------------- Massage bookings editor (weekly grid) ----------------
function MassageBookingsEditor() {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState({ bookings: [], slots: [] });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newBooking, setNewBooking] = useState(null); // {date, slot_id}

  const days = useMemo(() => (
    [0,1,2,3,4,5,6].map((i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; })
  ), [weekStart]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const from = isoLocal(days[0]);
      const to = isoLocal(days[6]);
      const res = await fetchAdminMassageBookings(from, to);
      setData(res);
    } catch (e) {
      toast({ title: "Errore", description: e?.message });
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);
  const { blockFor, reload: reloadBlocks } = useSlotBlocks("massage", isoLocal(days[0]), isoLocal(days[6]));

  const byDaySlot = useMemo(() => {
    const map = {};
    for (const b of data.bookings) {
      if (b.status === "cancelled") continue;
      map[`${b.date}::${b.slot_id}`] = b;
    }
    return map;
  }, [data.bookings]);

  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate()+7); setWeekStart(d); };
  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate()-7); setWeekStart(d); };
  const thisWeek = () => setWeekStart(mondayOf(new Date()));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="font-serif text-2xl font-bold text-[#2b1410]">Prenotazioni Massaggi</h2>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={prevWeek} className="rounded-md border border-[#e8dcc8] px-3 py-1.5 text-sm hover:bg-white" data-testid="massage-prev-week">← Settimana</button>
          <button onClick={thisWeek} className="rounded-md border border-[#e8dcc8] px-3 py-1.5 text-sm hover:bg-white" data-testid="massage-this-week">Oggi</button>
          <button onClick={nextWeek} className="rounded-md border border-[#e8dcc8] px-3 py-1.5 text-sm hover:bg-white" data-testid="massage-next-week">Settimana →</button>
          <ClosePeriodButton service="massage" onDone={reloadBlocks} testPrefix="massage" />
        </div>
      </div>
      <p className="text-sm text-[#7c5a4e] mb-3">
        Settimana dal <strong>{fmtItShort(days[0])}</strong> al <strong>{fmtItShort(days[6])}</strong>. Massaggio rilassante individuale, 60 min · € 60.
        <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-200 border border-emerald-400" /> libero</span>
        <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-200 border border-red-400" /> da confermare</span>
        <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-purple-200 border border-purple-400" /> confermata via WhatsApp</span>
        <span className="inline-flex items-center gap-1 ml-2"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-neutral-200 border border-neutral-400" /> chiuso</span>
      </p>
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
      ) : (
        <div className="overflow-x-auto border border-[#e8dcc8] rounded-xl bg-white">
          <table className="w-full text-xs sm:text-sm" data-testid="massage-week-grid">
            <thead>
              <tr className="bg-[#faf3ee]">
                <th className="p-2 text-left text-[#7c5a4e] font-semibold uppercase tracking-wide">Turno</th>
                {days.map((d, i) => (
                  <th key={i} className={`p-2 text-center ${d.toDateString() === new Date().toDateString() ? "bg-[#4a1c17]/10" : ""}`}>
                    <div className="text-[10px] text-[#7c5a4e] uppercase">{DOW_SHORT[d.getDay()]}</div>
                    <div className="font-serif text-[#2b1410]">{d.getDate()}/{d.getMonth()+1}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.slots || []).map((s) => (
                <tr key={s.id} className="border-t border-[#e8dcc8]">
                  <td className="p-2 font-semibold text-[#2b1410] whitespace-nowrap">{s.label}</td>
                  {days.map((d, di) => {
                    const iso = isoLocal(d);
                    const b = byDaySlot[`${iso}::${s.id}`];
                    return (
                      <td key={di} className="p-1">
                        {b ? (
                          <button
                            onClick={() => setSelected(b)}
                            className={`w-full rounded-md border px-2 py-1.5 text-left transition-colors ${
                              b.whatsapp_notified
                                ? "bg-purple-100 border-purple-300 hover:bg-purple-200 text-purple-900"
                                : "bg-red-100 border-red-300 hover:bg-red-200 text-red-900"
                            }`}
                            data-testid={`massage-cell-booked-${iso}-${s.id}`}
                            title={b.whatsapp_notified ? "Confermata via WhatsApp" : "Da confermare via WhatsApp"}
                          >
                            <div className="font-semibold text-[11px] truncate">{b.name}</div>
                            <div className="text-[10px] opacity-80 truncate">{b.phone}</div>
                          </button>
                        ) : blockFor(iso, s.id) ? (
                          <BlockedSlotCell block={blockFor(iso, s.id)} service="massage" iso={iso} slotId={s.id} testPrefix="massage" onChanged={reloadBlocks} />
                        ) : (
                          <FreeSlotCell service="massage" iso={iso} slotId={s.id} testPrefix="massage" onNewBooking={() => setNewBooking({ date: iso, slot_id: s.id })} onChanged={reloadBlocks} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <MassageBookingDetailModal booking={selected} slots={data.slots} bookings={data.bookings} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); load(); }} />
      )}
      {newBooking && (
        <NewMassageBookingModal init={newBooking} slots={data.slots} onClose={() => setNewBooking(null)} onCreated={() => { setNewBooking(null); load(); }} />
      )}
    </div>
  );
}

function MassageBookingDetailModal({ booking, slots, bookings, onClose, onChanged }) {
  const [mode, setMode] = useState("view"); // view | edit
  const [form, setForm] = useState({
    date: booking.date,
    slot_id: booking.slot_id,
    name: booking.name || "",
    email: booking.email || "",
    phone: booking.phone || "",
    notes: booking.notes || "",
  });
  const [saving, setSaving] = useState(false);

  // slots taken on the selected date (excluding this booking) — for the move dropdown
  const takenOnDate = useMemo(() => {
    const s = new Set();
    (bookings || []).forEach((b) => {
      if (b.id !== booking.id && b.status === "confirmed" && b.date === form.date) s.add(b.slot_id);
    });
    return s;
  }, [bookings, form.date, booking.id]);

  const cancel = async () => {
    if (!window.confirm("Confermare l'annullamento della prenotazione?")) return;
    try { await cancelMassageBooking(booking.id); toast({ title: "Prenotazione annullata" }); onChanged(); }
    catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e?.message }); }
  };
  const del = async () => {
    if (!window.confirm("Eliminare definitivamente questa prenotazione dallo storico?")) return;
    try { await deleteMassageBooking(booking.id); toast({ title: "Eliminata" }); onChanged(); }
    catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e?.message }); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await updateMassageBooking(booking.id, {
        date: form.date,
        slot_id: Number(form.slot_id),
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        num_people: 1,
        notes: form.notes.trim(),
      });
      toast({ title: "Prenotazione aggiornata" });
      onChanged();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  const markWaOpened = async () => {
    if (!booking.whatsapp_notified) {
      try {
        await updateMassageBooking(booking.id, { whatsapp_notified: true });
        onChanged();
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.warn("Failed to mark whatsapp_notified", err);
      }
    }
  };

  const slot = (slots || []).find((s) => s.id === booking.slot_id);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="massage-booking-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-serif text-xl font-bold text-[#2b1410]">
            {mode === "edit" ? "Modifica / Sposta massaggio" : "Prenotazione Massaggio"}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="h-4 w-4" /></button>
        </div>

        {mode === "view" ? (
          <>
            <div className="mt-3 space-y-2 text-sm text-[#2b1410]">
              <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#4a1c17]" /> <strong>{formatBookingDate(booking.date)}</strong></div>
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#4a1c17]" /> {slot?.label || `Turno #${booking.slot_id + 1}`} · 60 min</div>
              <div><span className="text-[#7c5a4e]">Trattamento:</span> Massaggio rilassante</div>
              <div><span className="text-[#7c5a4e]">Cliente:</span> <strong>{booking.name}</strong></div>
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#4a1c17]" /> <a href={`tel:${booking.phone}`} className="text-[#4a1c17] hover:underline">{booking.phone}</a></div>
              <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#4a1c17]" /> <a href={`mailto:${booking.email}`} className="text-[#4a1c17] hover:underline break-all">{booking.email}</a></div>
              <div><span className="text-[#7c5a4e]">Totale:</span> <strong>€ {booking.price}</strong></div>
              {booking.notes && <div className="pt-1 text-[13px] text-[#7c5a4e] border-t border-neutral-100"><em>Note:</em> {booking.notes}</div>}
              {booking.status === "cancelled" && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-xs">ANNULLATA</div>
              )}
              {booking.whatsapp_notified && booking.status !== "cancelled" && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-purple-100 text-purple-800 px-2 py-0.5 text-xs">
                  <MessageCircle className="h-3 w-3" /> Confermata via WhatsApp
                </div>
              )}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <a
                {...waAnchorProps(booking.whatsapp_url)}
                onClick={markWaOpened}
                data-testid="massage-whatsapp-btn"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white ${
                  booking.whatsapp_notified ? "bg-purple-600 hover:bg-purple-700" : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                <MessageCircle className="h-4 w-4" />
                {booking.whatsapp_notified ? "WhatsApp inviato" : "Invia WhatsApp"}
              </a>
              <button
                onClick={() => setMode("edit")}
                data-testid="massage-edit-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-2 text-sm font-medium"
              >
                <Pencil className="h-4 w-4" /> Modifica / Sposta
              </button>
              {booking.status !== "cancelled" && (
                <button
                  onClick={cancel}
                  data-testid="massage-cancel-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 text-sm font-medium"
                >
                  Annulla
                </button>
              )}
              <button
                onClick={del}
                data-testid="massage-delete-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-neutral-200 hover:bg-neutral-300 text-neutral-800 px-3 py-2 text-sm font-medium ml-auto"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </>
        ) : (
          <MassageBookingFormFields form={form} setForm={setForm} slots={slots} takenSlotIds={takenOnDate}
            actions={
              <div className="mt-5 flex flex-wrap gap-2">
                <button onClick={() => setMode("view")} className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200 px-4 py-2 text-sm">Annulla modifiche</button>
                <button onClick={save} disabled={saving} data-testid="massage-save-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-2 text-sm font-medium ml-auto disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
                </button>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}

function NewMassageBookingModal({ init, slots, onClose, onCreated }) {
  const [form, setForm] = useState({
    date: init.date,
    slot_id: init.slot_id,
    name: "", email: "", phone: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast({ title: "Nome e telefono obbligatori" });
      return;
    }
    setSaving(true);
    try {
      await adminCreateMassageBooking({
        date: form.date,
        slot_id: Number(form.slot_id),
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        num_people: 1,
        notes: form.notes.trim(),
      });
      toast({ title: "Prenotazione creata" });
      onCreated();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="massage-new-booking-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-serif text-xl font-bold text-[#2b1410]">Nuova prenotazione massaggio</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <MassageBookingFormFields
          form={form} setForm={setForm} slots={slots} takenSlotIds={new Set()}
          actions={
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200 px-4 py-2 text-sm">Annulla</button>
              <button onClick={create} disabled={saving} data-testid="massage-create-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-2 text-sm font-medium disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crea prenotazione
              </button>
            </div>
          }
        />
      </div>
    </div>
  );
}

function MassageBookingFormFields({ form, setForm, slots, takenSlotIds, actions }) {
  return (
    <div className="mt-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Data</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="massage-form-date"
            className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Turno</label>
          <select value={form.slot_id} onChange={(e) => setForm({ ...form, slot_id: Number(e.target.value) })}
            data-testid="massage-form-slot"
            className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white">
            {(slots || []).map((s) => (
              <option key={s.id} value={s.id} disabled={takenSlotIds && takenSlotIds.has(s.id)}>
                {s.label}{takenSlotIds && takenSlotIds.has(s.id) ? " (occupato)" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Nome cliente</label>
        <CustomerAutocomplete value={form.name} onChange={(v) => setForm({ ...form, name: v })} search={searchAdminCustomers} placeholder="Nome (cerca in archivio: hotel, spa, ristorante…)"
          testId="massage-form-name" className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white"
          onPick={(c) => { setForm({ ...form, name: c.name, phone: c.phone || form.phone, email: c.email || form.email }); toast({ title: `Cliente trovato: ${c.name}` }); }} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Telefono</label>
          <CustomerAutocomplete value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} search={searchAdminCustomers} placeholder="+39…" inputMode="tel" minChars={3}
            testId="massage-form-phone" className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white"
            onPick={(c) => { setForm({ ...form, name: c.name, phone: c.phone || form.phone, email: c.email || form.email }); toast({ title: `Cliente trovato: ${c.name}` }); }} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Email (opz.)</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="massage-form-email"
            className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white" />
        </div>
      </div>
      <div className="rounded-md bg-[#4a1c17] text-[#f7ebdf] px-3 py-2 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider opacity-70">Massaggio rilassante · 60 min</div>
          <div className="text-[10px] opacity-70">Individuale</div>
        </div>
        <div className="text-lg font-serif font-bold" data-testid="massage-form-total">€ 60</div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Note</label>
        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
          data-testid="massage-form-notes"
          className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white" />
      </div>
      {actions}
    </div>
  );
}




function HorseBookingsEditor() {
  const [bookings, setBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [operator, setOperator] = React.useState({ name: "", phone: "" });
  const [showOpSettings, setShowOpSettings] = React.useState(false);
  const [savingOp, setSavingOp] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const tok = localStorage.getItem("admin_token");
      const today = new Date().toISOString().slice(0, 10);
      const [rBook, rOp] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/horse/bookings${showHistory ? "" : `?from_date=${today}`}`, { headers: { Authorization: `Bearer ${tok}` } }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/horse/operator`, { headers: { Authorization: `Bearer ${tok}` } }),
      ]);
      const data = await rBook.json();
      const op = await rOp.json();
      setBookings(data.bookings || []);
      setOperator({ name: op.name || "", phone: op.phone || "" });
    } finally {
      setLoading(false);
    }
  }, [showHistory]);
  React.useEffect(() => { load(); }, [load]);

  const saveOperator = async () => {
    setSavingOp(true);
    try {
      const tok = localStorage.getItem("admin_token");
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/horse/operator`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" },
        body: JSON.stringify(operator),
      });
      setShowOpSettings(false);
    } finally {
      setSavingOp(false);
    }
  };

  const setStatus = async (id, status) => {
    const tok = localStorage.getItem("admin_token");
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/horse/bookings/${id}`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare la prenotazione?")) return;
    const tok = localStorage.getItem("admin_token");
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/horse/bookings/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${tok}` },
    });
    load();
  };

  const sendWhatsapp = async (id, party = "guest") => {
    const tok = localStorage.getItem("admin_token");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/horse/bookings/${id}/whatsapp?party=${party}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      const data = await r.json();
      if (data.wa_url) {
        openWhatsApp(data.wa_url);
        load(); // refresh so button turns purple
      } else {
        alert(party === "operator" ? "Numero operatore non configurato." : "Il cliente non ha lasciato il numero di telefono.");
      }
    } catch (e) {
      alert("Impossibile aprire WhatsApp. Riprova.");
    }
  };

  if (loading) return <div className="p-6 text-center text-[#7a5a48]"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento…</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Sunset className="h-5 w-5 text-[#4a1c17]" />
        <h2 className="font-serif text-xl text-[#2b1410]">Prenotazioni Cavallo</h2>
        <button
          onClick={() => setShowHistory((v) => !v)}
          data-testid="horse-history-toggle"
          className={`text-xs rounded-full px-3 py-1 border ${showHistory ? "bg-[#4a1c17] text-white border-[#4a1c17]" : "border-[#e8dcc8] text-[#4a1c17] bg-white"}`}
        >
          {showHistory ? "Nascondi storico" : "Mostra storico"}
        </button>
        <button
          onClick={() => setShowOpSettings((v) => !v)}
          className="ml-auto text-xs text-[#4a1c17] underline flex items-center gap-1"
          data-testid="horse-op-settings-toggle"
        >
          <Key className="h-3 w-3" /> Operatore: {operator.name || "—"} · {operator.phone || "—"}
        </button>
        <button onClick={load} className="text-xs text-[#4a1c17] underline">Aggiorna</button>
      </div>
      {showOpSettings && (
        <div className="rounded-xl bg-[#faf3ee] border border-[#e8dcc8] p-4">
          <div className="text-xs text-[#7a5a48] mb-2">
            Numero e nome dell'operatore esterno che riceve le notifiche via WhatsApp per ogni prenotazione cavallo.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              value={operator.name}
              onChange={(e) => setOperator((o) => ({ ...o, name: e.target.value }))}
              placeholder="Nome"
              className="rounded-md border border-[#e8dcc8] bg-white px-3 py-1.5 text-sm"
              data-testid="horse-op-name-input"
            />
            <input
              value={operator.phone}
              onChange={(e) => setOperator((o) => ({ ...o, phone: e.target.value }))}
              placeholder="+39 339 652 4946"
              className="rounded-md border border-[#e8dcc8] bg-white px-3 py-1.5 text-sm"
              data-testid="horse-op-phone-input"
            />
          </div>
          <button
            onClick={saveOperator}
            disabled={savingOp}
            className="mt-2 px-3 py-1.5 rounded-full bg-[#4a1c17] text-white text-xs disabled:opacity-60"
            data-testid="horse-op-save"
          >
            {savingOp ? "Salvataggio…" : "Salva"}
          </button>
        </div>
      )}
      <HorseClosures />
      {bookings.length === 0 && (
        <div className="rounded-xl bg-white border border-[#e8dcc8] p-6 text-center text-sm text-[#7a5a48]">
          Nessuna prenotazione futura.
        </div>
      )}
      {bookings.map((b) => (
        <div key={b.id} className="rounded-xl bg-white border border-[#e8dcc8] p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-serif font-semibold text-[#2b1410]">{b.name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${
                  b.status === "confirmed" ? "bg-emerald-100 text-emerald-800" :
                  b.status === "cancelled" ? "bg-rose-100 text-rose-800" :
                  "bg-amber-100 text-amber-800"
                }`}>{b.status || "pending"}</span>
              </div>
              <div className="text-xs text-[#7a5a48]">
                📅 {b.date} · 🕐 {b.slot_start}–{b.slot_end} (tramonto {b.sunset}) · 👥 {b.people} pers.
              </div>
              <div className="text-xs text-[#4a1c17] mt-1">
                💰 € {b.total || (b.people * 30)} · ☎️ <a href={`tel:${b.phone}`} className="underline">{b.phone}</a>
                {b.email && <> · ✉️ <a href={`mailto:${b.email}`} className="underline">{b.email}</a></>}
              </div>
              {b.notes && <div className="text-xs text-[#7a5a48] mt-1 italic">Note: {b.notes}</div>}
            </div>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => sendWhatsapp(b.id, "guest")}
                className={`px-2 py-1 rounded-full text-white text-[10px] font-semibold ${
                  b.wa_guest_sent_at ? "bg-purple-600 hover:bg-purple-700" : "bg-[#25D366] hover:bg-[#128C7E]"
                }`}
                data-testid={`horse-wa-guest-${b.id}`}
                title={b.wa_guest_sent_at ? `Inviato al cliente il ${new Date(b.wa_guest_sent_at).toLocaleString('it-IT')}` : "Invia WA al cliente"}
              >
                <MessageCircle className="h-3 w-3 inline mr-0.5" />
                {b.wa_guest_sent_at ? "✓ Cliente" : "WA Cliente"}
              </button>
              <button
                onClick={() => sendWhatsapp(b.id, "operator")}
                className={`px-2 py-1 rounded-full text-white text-[10px] font-semibold ${
                  b.wa_operator_sent_at ? "bg-purple-600 hover:bg-purple-700" : "bg-[#25D366] hover:bg-[#128C7E]"
                }`}
                data-testid={`horse-wa-operator-${b.id}`}
                title={b.wa_operator_sent_at ? `Inviato all'operatore il ${new Date(b.wa_operator_sent_at).toLocaleString('it-IT')}` : `Invia WA a ${operator.name || 'operatore'}`}
              >
                <MessageCircle className="h-3 w-3 inline mr-0.5" />
                {b.wa_operator_sent_at ? "✓ " + (operator.name || "Riccardo") : "WA " + (operator.name || "Riccardo")}
              </button>
              {b.status !== "confirmed" && (
                <button onClick={() => setStatus(b.id, "confirmed")} className="px-2 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[10px]" data-testid={`horse-confirm-${b.id}`}>
                  <CheckCircle2 className="h-3 w-3 inline mr-0.5" />Conferma
                </button>
              )}
              {b.status !== "cancelled" && (
                <button onClick={() => setStatus(b.id, "cancelled")} className="px-2 py-1 rounded-full bg-white border border-[#e8dcc8] text-[#4a1c17] text-[10px]" data-testid={`horse-cancel-${b.id}`}>
                  <X className="h-3 w-3 inline mr-0.5" />Annulla
                </button>
              )}
              <button onClick={() => remove(b.id)} className="px-2 py-1 rounded-full text-rose-700 hover:bg-rose-50 text-[10px]" data-testid={`horse-delete-${b.id}`}>
                <Trash2 className="h-3 w-3 inline" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
